import { getAPIURL } from "./config";

/**
 * Вспомогательная функция для обработки нулевых значений
 * Сохраняет 0 если сервер вернул 0, заменяет только null/undefined
 */
const safeNumber = (value, defaultValue = 0) => {
  if (value === null || value === undefined) {
    return defaultValue;
  }
  return value;
};

/**
 * ОБНОВЛЕННЫЙ API ДЛЯ РАБОТЫ СО СМЕТОЙ:
 *
 * 1. Добавлена функция getProjectEstimate() - основной эндпоинт для получения всех данных сметы
 * 2. Созданы функции createEstimateContext() и getSectionContext() для правильного сбора контекста
 * 3. Удалены все хардкодированные значения (400000, 30, 4, 5, 65, 50, 7, 1, 15 и т.д.)
 * 4. Функции теперь используют реальные данные из API вместо хардкода
 * 5. Сохранена обратная совместимость через legacy функции
 *
 * НОВЫЕ ФУНКЦИИ:
 * - getProjectEstimate(projectId) - получить все данные сметы
 * - createEstimateContext(projectId) - создать полный контекст для работы со сметой
 * - getSectionContext(projectId, sectionId) - получить контекст для конкретной секции
 *
 * ИСПОЛЬЗОВАНИЕ:
 * const context = await createEstimateContext(projectId);
 * const estimateData = context.estimate;
 * const sectionsData = context.sections;
 */

/** Порядок блоков: Паркинг, затем "Блок 1", "Блок 2", ... по возрастанию, затем остальные. */
const sortBlockExpendituresOrder = (blockExpenditures) => {
  if (!Array.isArray(blockExpenditures) || blockExpenditures.length === 0)
    return blockExpenditures;
  const blockRegex = /Блок\s*(\d+)/i;
  const isParking = (name) => (name || "").toLowerCase().trim() === "паркинг";
  const withMeta = blockExpenditures.map((item) => {
    const name = (item?.block?.name || "").trim();
    const parking = isParking(name);
    const m = name.match(blockRegex);
    const num = m ? parseInt(m[1], 10) : null;
    return { item, parking, num };
  });
  const parking = withMeta.filter((x) => x.parking).map((x) => x.item);
  const numbered = withMeta
    .filter((x) => !x.parking && x.num !== null)
    .sort((a, b) => a.num - b.num)
    .map((x) => x.item);
  const rest = withMeta
    .filter((x) => !x.parking && x.num === null)
    .map((x) => x.item);
  return [...parking, ...numbered, ...rest];
};

const UNIFIED_SECTION_NAMES = new Set(["инсоляция", "гп"]);
const BIM_MANAGER_ROLE_NAME = "bim менеджер";

export const isUnifiedSectionName = (sectionName) =>
  UNIFIED_SECTION_NAMES.has(sectionName?.toLowerCase().trim());

const toFiniteNumber = (value) => {
  if (value === undefined || value === null || value === "") return undefined;
  const n = typeof value === "number" ? value : parseFloat(value);
  return Number.isFinite(n) ? n : undefined;
};

/** Трудозатраты единых разделов: не отдаём 0, если есть ненулевой direct/total. */
export const pickUnifiedDayEfforts = (dayEfforts) => {
  if (dayEfforts == null || dayEfforts === "") return undefined;
  if (typeof dayEfforts === "number") return toFiniteNumber(dayEfforts);
  const direct = toFiniteNumber(dayEfforts.direct);
  const total = toFiniteNumber(dayEfforts.total);
  if (direct !== undefined && direct !== 0) return direct;
  if (total !== undefined && total !== 0) return total;
  if (direct !== undefined) return direct;
  if (total !== undefined) return total;
  return undefined;
};

export const firstNonEmptyList = (...lists) => {
  for (const list of lists) {
    if (Array.isArray(list) && list.length > 0) return list;
  }
  return [];
};

export const resolveBimRoleSectionId = (role, sectionsByName) => {
  if (role?.section_id != null) return role.section_id;
  const name = role?.role?.trim();
  if (!name) return null;
  if (name.toLowerCase() === BIM_MANAGER_ROLE_NAME) return null;
  const withoutPrefix = name.replace(/^BIM\s+/i, "").trim();
  return (
    sectionsByName.get(name) ?? sectionsByName.get(withoutPrefix) ?? null
  );
};

const mergeCalculatedModuleRoles = (contextRoles, apiRoles) => {
  const fromCtx = Array.isArray(contextRoles) ? contextRoles : [];
  const fromApi = Array.isArray(apiRoles) ? apiRoles : [];
  if (!fromCtx.length) return fromApi;
  const apiByName = new Map(
    fromApi.map((role) => [role?.role?.trim(), role]),
  );
  return fromCtx.map((ourRole) => {
    const fromApiRole = apiByName.get(ourRole?.role?.trim());
    if (!fromApiRole) return ourRole;
    const ourSalary = toFiniteNumber(ourRole.average_salary);
    return {
      ...fromApiRole,
      ...ourRole,
      day_efforts: fromApiRole.day_efforts ?? ourRole.day_efforts,
      money_expenditures:
        fromApiRole.money_expenditures ?? ourRole.money_expenditures,
      section_id: fromApiRole.section_id ?? ourRole.section_id ?? null,
      average_salary:
        ourSalary !== undefined && ourSalary !== 0
          ? ourRole.average_salary
          : (fromApiRole.average_salary ?? ourRole.average_salary),
    };
  });
};

/**
 * Собрать карту деталей секций из construction_design_detailed (один GET сметы).
 * Формат как у getSectionConstructionDesign: bonus_percent, average_salary, block_expenditures, section_id.
 * Блоки в block_expenditures: Паркинг → Блок 1,2,… → остальные.
 */
export const buildSectionDetailsFromDetailed = (constructionDesignDetailed) => {
  const sectionsData = {};
  (constructionDesignDetailed || []).forEach((detail) => {
    const sectionId = detail.section_id;
    if (sectionId == null) return;
    const rawBlocks = detail.block_expenditures || [];
    sectionsData[sectionId] = {
      bonus_percent: detail.bonus_percent ?? 0,
      average_salary: detail.average_salary ?? 0,
      block_expenditures: sortBlockExpendituresOrder(rawBlocks),
      section_id: sectionId,
    };
  });
  return sectionsData;
};

// Вспомогательная функция для получения реальных данных секций (один GET сметы, без вызовов по секциям)
export const getRealSectionData = async (projectId, sections) => {
  try {
    const data = await getProjectEstimate(projectId);
    const fromDetailed = buildSectionDetailsFromDetailed(
      data.construction_design_detailed,
    );
    const realSectionData = {};
    for (const section of sections || []) {
      const sid = section.section?.id;
      if (sid == null) continue;
      realSectionData[sid] = fromDetailed[sid] || {
        bonus_percent: 0,
        average_salary: 0,
        block_expenditures: [],
        section_id: sid,
      };
    }
    return realSectionData;
  } catch (err) {
    console.error("Error fetching section data from estimate:", err);
    const realSectionData = {};
    for (const section of sections || []) {
      const sid = section.section?.id;
      if (sid != null) {
        realSectionData[sid] = {
          bonus_percent: 0,
          average_salary: 0,
          block_expenditures: [],
          section_id: sid,
        };
      }
    }
    return realSectionData;
  }
};

/**
 * Создать контекст для работы со сметой (загрузка сметы + деталей секций).
 * Использует GET /project_estimates/projects/{project_id} с опциональным ?version=
 * @param {number} projectId - ID проекта
 * @param {number|null} [version] - Опциональный номер версии сметы
 */
export const createEstimateContext = async (projectId, version = null) => {
  try {
    const estimateData = await getProjectEstimate(projectId, version);

    // Объединяем разделы из construction_design и construction_design_project
    const allSections = [
      ...(estimateData?.construction_design || []),
      ...(estimateData?.construction_design_project || []),
    ];

    console.log(
      "[createEstimateContext] Загружено разделов из estimateData:",
      allSections.length,
    );
    console.log(
      "[createEstimateContext] Разделы из construction_design:",
      estimateData?.construction_design?.length || 0,
    );
    console.log(
      "[createEstimateContext] Разделы из construction_design_project:",
      estimateData?.construction_design_project?.length || 0,
    );
    console.log(
      "[createEstimateContext] Разделы:",
      allSections.map((s) => ({
        id: s.section?.id,
        name: s.section?.name,
      })),
    );

    // Сохраняем список ID разделов для сравнения после сохранения
    const sectionIdsBefore = new Set(
      allSections.map((s) => s.section?.id).filter(Boolean),
    );
    console.log(
      "[createEstimateContext] ID разделов до сохранения:",
      Array.from(sectionIdsBefore).sort((a, b) => a - b),
    );

    // Детали секций: из одного GET (construction_design_detailed), без вызовов GET /sections/{id}
    let sectionsData = buildSectionDetailsFromDetailed(
      estimateData.construction_design_detailed,
    );

    // Обогащаем sectionsData названиями разделов из allSections и дополняем отсутствующие разделы
    for (const section of allSections) {
      const sectionId = section.section?.id;
      if (!sectionId) continue;
      const sectionNameRaw = section.section?.name;
      const sectionName = sectionNameRaw?.toLowerCase().trim();
      const isUnified = sectionName === "инсоляция" || sectionName === "гп";

      const fromRowEfforts = pickUnifiedDayEfforts(section.day_efforts);
      const fromRowSalary = toFiniteNumber(section.average_salary);

      if (sectionsData[sectionId]) {
        const existing = sectionsData[sectionId];
        sectionsData[sectionId] = {
          ...existing,
          section_name: existing.section_name || sectionNameRaw,
          ...(isUnified && {
            total_day_efforts:
              pickUnifiedDayEfforts({
                direct: existing.total_day_efforts,
                total: fromRowEfforts,
              }) ??
              fromRowEfforts ??
              existing.total_day_efforts ??
              0,
            average_salary:
              (toFiniteNumber(existing.average_salary) &&
              toFiniteNumber(existing.average_salary) !== 0
                ? existing.average_salary
                : fromRowSalary) ??
              existing.average_salary ??
              0,
          }),
        };
      } else {
        // Раздела нет в detailed (единые разделы ГП, Инсоляция и т.д.) — берём значения из строки GET
        sectionsData = {
          ...sectionsData,
          [sectionId]: {
            bonus_percent: section.bonus_percent ?? 0,
            average_salary: fromRowSalary ?? 0,
            block_expenditures: [],
            section_id: sectionId,
            section_name: sectionNameRaw,
            ...(isUnified && { total_day_efforts: fromRowEfforts ?? 0 }),
          },
        };
      }
    }

    console.log(
      "[createEstimateContext] Загружено деталей секций (из construction_design_detailed):",
      Object.keys(sectionsData).length,
    );

    // Обновляем estimateData, объединяя разделы для обратной совместимости.
    // include_bonuses для рабочего проектирования: используем значение с бэкенда или true по умолчанию.
    const updatedEstimateData = {
      ...estimateData,
      construction_design: allSections,
      include_bonuses: estimateData.include_bonuses ?? true,
    };

    return {
      estimate: updatedEstimateData,
      sections: sectionsData,
      projectId,
    };
  } catch (error) {
    console.error("Error creating estimate context:", error);
    throw error;
  }
};

// Получить контекст для конкретной секции (один GET сметы, секция из construction_design_detailed)
export const getSectionContext = async (projectId, sectionId) => {
  try {
    const estimateData = await getProjectEstimate(projectId);
    const sectionsData = buildSectionDetailsFromDetailed(
      estimateData.construction_design_detailed,
    );
    const sectionData = sectionsData[sectionId] ||
      sectionsData[parseInt(sectionId, 10)] || {
        bonus_percent: 0,
        average_salary: 0,
        block_expenditures: [],
        section_id: sectionId,
      };

    const allSections = [
      ...(estimateData?.construction_design || []),
      ...(estimateData?.construction_design_project || []),
    ];
    const updatedEstimateData = {
      ...estimateData,
      construction_design: allSections,
    };

    return {
      estimate: updatedEstimateData,
      section: sectionData,
      projectId,
      sectionId,
    };
  } catch (error) {
    console.error("Error getting section context:", error);
    throw error;
  }
};

// Получить типы информации для CP TEI
export const getCpTeiInfTypes = async () => {
  try {
    const response = await fetch(
      `${getAPIURL()}project_estimates/cp_tei_inf_types`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching CP TEI inf types:", error);
    throw error;
  }
};

// Получить типы инженерных помещений для CP TEI
export const getCpTeiEngrooms = async () => {
  try {
    const response = await fetch(
      `${getAPIURL()}project_estimates/cp_tei_engrooms`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching CP TEI engrooms:", error);
    throw error;
  }
};

// Получить все секции проекта
export const getAllSections = async () => {
  try {
    const response = await fetch(`${getAPIURL()}projects/sections/all`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching all sections:", error);
    throw error;
  }
};

// Добавить новый раздел проекта
export const addProjectSection = async (name) => {
  try {
    const response = await fetch(`${getAPIURL()}projects/sections/add`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ name }),
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // ignore
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error adding project section:", error);
    throw error;
  }
};

// Удалить разделы проекта из сметы
export const deleteProjectSections = async (projectId, sectionIds) => {
  try {
    if (!sectionIds || sectionIds.length === 0) {
      return { message: "Нет разделов для удаления" };
    }

    if (!projectId) {
      throw new Error("projectId не указан");
    }

    const url = `${getAPIURL()}project_estimates/projects/${projectId}/sections/${
      sectionIds[0]
    }/delete`;
    console.log("DELETE запрос на URL:", url);
    console.log("Payload:", { section_ids: sectionIds });

    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ section_ids: sectionIds }),
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // ignore
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error deleting project sections:", error);
    throw error;
  }
};

// Получить данные CP TEI для проекта
// @param {number} projectId - ID проекта
// @param {number|null} version - Опциональный номер версии для получения конкретной версии
export const getCpTeis = async (projectId, version = null) => {
  try {
    // Добавляем timestamp для предотвращения кэширования
    const timestamp = new Date().getTime();

    // Формируем URL с опциональным параметром version
    let url = `${getAPIURL()}project_estimates/cp_teis/${projectId}?_t=${timestamp}`;
    if (version !== null && version !== undefined) {
      url += `&version=${version}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    if (!String(error.message || "").includes("404")) {
      console.error("Error fetching CP TEIs:", error);
    }
    throw error;
  }
};

// Рассчитать общую площадь CP TEI
export const calculateTotalArea = async (data) => {
  try {
    const response = await fetch(
      `${getAPIURL()}project_estimates/cp_teis/calculate_total_area`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(data),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;

      try {
        const errorData = await response.json();
        console.error("calculateTotalArea - ошибка сервера:", errorData);
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          console.error("calculateTotalArea - текст ошибки:", errorText);
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }

      throw new Error(errorMessage);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error("Error calculating total area:", error);
    throw error;
  }
};

// Добавить новый блок CP TEI
export const addCpTei = async (data) => {
  try {
    // Проверяем и нормализуем данные перед отправкой
    const normalizedData = {
      ...data,
      floor_count: parseInt(data.floor_count) || 0,
      apartments_count: parseInt(data.apartments_count) || 0,
      basement_area: parseFloat(data.basement_area) || 0,
      total_area: parseFloat(data.total_area) || 0,
      inf_type_id: parseInt(data.inf_type_id) || 0,
      project_id: parseInt(data.project_id) || 0,
      block_id: parseInt(data.block_id) || 0,
      eng_room_ids: Array.isArray(data.eng_room_ids)
        ? data.eng_room_ids.map((id) => parseInt(id))
        : [],
      typical_floors: Array.isArray(data.typical_floors)
        ? data.typical_floors.map((floor) => ({
            floor_number_from: parseInt(floor.floor_number_from) || 0,
            floor_number_to: parseInt(floor.floor_number_to) || 0,
            area: parseFloat(floor.area) || 0,
            floors_count: parseInt(floor.floors_count) || 0,
            total_area: parseFloat(floor.total_area) || 0,
          }))
        : [],
      typical_floors_area: parseFloat(data.typical_floors_area) || 0,
      typical_floors_count: parseInt(data.typical_floors_count) || 0,
      non_typical_floors: Array.isArray(data.non_typical_floors)
        ? data.non_typical_floors.map((floor) => ({
            commercial_area: parseFloat(floor.commercial_area) || 0,
            other_area: parseFloat(floor.other_area) || 0,
            floor_number: parseInt(floor.floor_number) || 0,
            total_area: parseFloat(floor.total_area) || 0,
          }))
        : [],
      ...(Number.isFinite(Number.parseFloat(data.floor_coeff))
        ? { floor_coeff: Number.parseFloat(data.floor_coeff) }
        : {}),
    };

    // Для паркинга и части блоков типовые этажи могут быть пустыми — это допустимо, не логируем предупреждение.

    const response = await fetch(
      `${getAPIURL()}project_estimates/cp_teis/add`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(normalizedData),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        // Если не удалось получить JSON, используем текст
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }

      throw new Error(errorMessage);
    }

    const responseData = await response.json();

    // Проверяем структуру ответа
    if (responseData && responseData.data && responseData.data.id) {
    } else {
      console.warn("Неожиданная структура ответа от сервера:", responseData);
    }

    return responseData;
  } catch (error) {
    console.error("Error adding CP TEI:", error);
    throw error;
  }
};

// Обновить существующий блок CP TEI
export const updateCpTei = async (cpTeiId, data) => {
  try {
    // Дополнительная валидация входных данных
    if (
      !cpTeiId ||
      cpTeiId === "local" ||
      cpTeiId === "temp" ||
      cpTeiId < 0 ||
      String(cpTeiId).length > 13
    ) {
      console.error("Обнаружен некорректный ID блока:", cpTeiId);
      console.error("Возможно, это временный ID, созданный через Date.now()");
      throw new Error(
        `Некорректный ID блока: ${cpTeiId}. Попробуйте перезагрузить страницу.`,
      );
    }

    // Проверяем и нормализуем данные перед отправкой
    const normalizedData = {
      ...data,
      floor_count: parseInt(data.floor_count) || 0,
      apartments_count: parseInt(data.apartments_count) || 0,
      basement_area: parseFloat(data.basement_area) || 0,
      total_area: parseFloat(data.total_area) || 0,
      inf_type_id: parseInt(data.inf_type_id) || 0,
      project_id: parseInt(data.project_id) || 0,
      block_id: parseInt(data.block_id) || 0,
      eng_room_ids: Array.isArray(data.eng_room_ids)
        ? data.eng_room_ids.map((id) => parseInt(id))
        : [],
      typical_floors: Array.isArray(data.typical_floors)
        ? data.typical_floors.map((floor) => ({
            floor_number_from: parseInt(floor.floor_number_from) || 0,
            floor_number_to: parseInt(floor.floor_number_to) || 0,
            area: parseFloat(floor.area) || 0,
            floors_count: parseInt(floor.floors_count) || 0,
            total_area: parseFloat(floor.total_area) || 0,
          }))
        : [],
      typical_floors_area: parseFloat(data.typical_floors_area) || 0,
      typical_floors_count: parseInt(data.typical_floors_count) || 0,
      non_typical_floors: Array.isArray(data.non_typical_floors)
        ? data.non_typical_floors.map((floor) => ({
            commercial_area: parseFloat(floor.commercial_area) || 0,
            other_area: parseFloat(floor.other_area) || 0,
            floor_number: parseInt(floor.floor_number) || 0,
            total_area: parseFloat(floor.total_area) || 0,
          }))
        : [],
      ...(Number.isFinite(Number.parseFloat(data.floor_coeff))
        ? { floor_coeff: Number.parseFloat(data.floor_coeff) }
        : {}),
    };

    // Примечание: сервер требует project_id и block_id даже при обновлении
    // Поэтому не удаляем эти поля

    const response = await fetch(
      `${getAPIURL()}project_estimates/cp_teis/${cpTeiId}/update`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(normalizedData),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      let errorData = null;

      try {
        errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        // Если не удалось получить JSON, используем текст
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }

      // Дополнительная диагностика для 500 ошибок
      if (response.status === 500) {
        console.error("=== ДИАГНОСТИКА 500 ОШИБКИ ===");
        console.error(
          "URL:",
          `${getAPIURL()}project_estimates/cp_teis/${cpTeiId}/update`,
        );
        console.error("ID блока:", cpTeiId);
        console.error(
          "Отправленные данные:",
          JSON.stringify(normalizedData, null, 2),
        );
        console.error("Заголовки запроса:", {
          "Content-Type": "application/json",
          credentials: "include",
        });
        console.error("================================");
      }

      console.error(`Ошибка обновления CP TEI (${response.status}):`);
      console.error(
        "Отправленные данные:",
        JSON.stringify(normalizedData, null, 2),
      );

      // Анализируем ошибку для предоставления более полезной информации
      if (errorData) {
        if (errorData.errors) {
          console.error("Детали ошибок валидации:", errorData.errors);
        }
        if (errorData.message) {
          console.error("Сообщение об ошибке:", errorData.message);
        }
        if (errorData.details) {
          console.error("Детали ошибки:", errorData.details);
        }
      }

      throw new Error(errorMessage);
    }

    const responseData = await response.json();

    // Проверяем структуру ответа
    if (responseData && responseData.data && responseData.data.id) {
    } else {
      console.warn(
        "Неожиданная структура ответа от сервера при обновлении:",
        responseData,
      );
    }

    return responseData;
  } catch (error) {
    console.error("Error updating CP TEI:", error);
    throw error;
  }
};

// Обновить коэффициент этажности блока (PATCH cp_teis/{block_id})
export const patchCpTeiFloorCoeff = async (blockId, value) => {
  try {
    const normalizedValue = Number.parseFloat(value);
    const response = await fetch(
      `${getAPIURL()}project_estimates/cp_teis/${blockId}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          coefficient: Number.isFinite(normalizedValue) ? normalizedValue : 0,
        }),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error patching CP TEI floor coeff:", error);
    throw error;
  }
};

// Удалить блок CP TEI
export const deleteCpTei = async (cpTeiId) => {
  try {
    const response = await fetch(
      `${getAPIURL()}project_estimates/cp_teis/${cpTeiId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        // Если не удалось получить JSON, используем текст
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    // Для DELETE запроса может не быть тела ответа
    if (response.status === 204) {
      return { success: true };
    }

    return await response.json();
  } catch (error) {
    console.error("Error deleting CP TEI:", error);
    throw error;
  }
};

// Состав разделов, который уже видели в этой сессии.
// Короткий ответ калькуляции или сохранения не должен его забывать:
// следующий запрос снова отправит полный список, а не урезанный.
const estimateSectionMemory = new Map();
const pendingDeletedSections = new Map();

function projectMemoryKey(projectId) {
  return String(projectId);
}

function readMemorySectionId(row) {
  const id = Number(row?.section?.id ?? row?.section_id);
  return Number.isFinite(id) ? id : null;
}

function getPendingDeletedSet(projectId) {
  return pendingDeletedSections.get(projectMemoryKey(projectId)) || new Set();
}

export function isSectionPendingDelete(projectId, sectionId) {
  const id = Number(sectionId);
  if (!Number.isFinite(id)) return false;
  return getPendingDeletedSet(projectId).has(id);
}

export function markSectionsPendingDelete(projectId, sectionIds) {
  const key = projectMemoryKey(projectId);
  const set = new Set(pendingDeletedSections.get(key) || []);
  (sectionIds || []).forEach((sectionId) => {
    const id = Number(sectionId);
    if (Number.isFinite(id)) set.add(id);
  });
  pendingDeletedSections.set(key, set);
}

export function clearPendingDeletedSections(projectId, sectionIds) {
  const key = projectMemoryKey(projectId);
  const set = new Set(pendingDeletedSections.get(key) || []);
  (sectionIds || []).forEach((sectionId) => {
    set.delete(Number(sectionId));
  });
  if (set.size > 0) pendingDeletedSections.set(key, set);
  else pendingDeletedSections.delete(key);
}

export function commitDeletedSections(projectId, sectionIds) {
  const key = projectMemoryKey(projectId);
  const ids = new Set(
    (sectionIds || [])
      .map((sectionId) => Number(sectionId))
      .filter((id) => Number.isFinite(id)),
  );
  const current = estimateSectionMemory.get(key);
  if (current && ids.size > 0) {
    const keepRow = (row) => {
      const id = readMemorySectionId(row);
      return id == null || !ids.has(id);
    };
    estimateSectionMemory.set(key, {
      construction_design: (current.construction_design || []).filter(keepRow),
      construction_design_detailed: (
        current.construction_design_detailed || []
      ).filter((detail) => {
        const id = Number(detail?.section_id);
        return !Number.isFinite(id) || !ids.has(id);
      }),
    });
  }
  clearPendingDeletedSections(projectId, sectionIds);
}

export function resetEstimateSectionMemory(projectId) {
  const key = projectMemoryKey(projectId);
  estimateSectionMemory.delete(key);
  pendingDeletedSections.delete(key);
}

function unionMemoryRows(prevRows, nextRows) {
  const map = new Map();
  const put = (row) => {
    const id = readMemorySectionId(row);
    if (id == null) return;
    const existing = map.get(id);
    if (!existing) {
      map.set(id, row);
      return;
    }
    map.set(id, {
      ...existing,
      ...row,
      section: row?.section?.id != null ? row.section : existing.section,
      day_efforts: row?.day_efforts ?? existing.day_efforts,
      money_expenditures: row?.money_expenditures ?? existing.money_expenditures,
      expenditures_percent:
        row?.expenditures_percent ?? existing.expenditures_percent,
    });
  };
  (prevRows || []).forEach(put);
  (nextRows || []).forEach(put);
  return [...map.values()];
}

function unionMemoryDetailed(prevList, nextList) {
  const map = new Map();
  const put = (detail) => {
    const id = Number(detail?.section_id);
    if (!Number.isFinite(id)) return;
    const existing = map.get(id);
    if (!existing) {
      map.set(id, detail);
      return;
    }
    const existingBlocks = Array.isArray(existing.block_expenditures)
      ? existing.block_expenditures
      : [];
    const nextBlocks = Array.isArray(detail.block_expenditures)
      ? detail.block_expenditures
      : [];
    map.set(id, {
      ...existing,
      ...detail,
      block_expenditures: nextBlocks.length > 0 ? nextBlocks : existingBlocks,
    });
  };
  (prevList || []).forEach(put);
  (nextList || []).forEach(put);
  return [...map.values()];
}

function rememberEstimateSections(projectId, estimate) {
  if (projectId == null || !estimate) return;
  const key = projectMemoryKey(projectId);
  const prev = estimateSectionMemory.get(key);
  estimateSectionMemory.set(key, {
    construction_design: unionMemoryRows(prev?.construction_design, [
      ...(estimate.construction_design || []),
      ...(estimate.construction_design_project || []),
    ]),
    construction_design_detailed: unionMemoryDetailed(
      prev?.construction_design_detailed,
      estimate.construction_design_detailed,
    ),
  });
}

function getRememberedEstimate(projectId) {
  return estimateSectionMemory.get(projectMemoryKey(projectId)) || null;
}

function sectionsLostAfterWriteError(labels) {
  return new Error(
    `Сервер удалил разделы после сохранения: ${labels}. Они ещё есть на экране. Нажмите «Сохранить» ещё раз и не обновляйте страницу, пока сохранение не пройдёт без этой ошибки.`,
  );
}

/**
 * Получить смету проекта (Get Smeta).
 * GET /project_estimates/projects/{project_id}
 * @param {number} projectId - ID проекта (path)
 * @param {number|null} [version] - Опциональный номер версии (query)
 * @returns {Promise<Object>} Ответ: editable, version, construction_design, bim_coordination, quality_control, contract_sections и др.
 */
export const getProjectEstimate = async (projectId, version = null) => {
  try {
    let url = `${getAPIURL()}project_estimates/projects/${projectId}`;
    if (version !== null && version !== undefined) {
      url += `?version=${version}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    if (version == null) {
      rememberEstimateSections(projectId, data);
    }
    return data;
  } catch (error) {
    // 404 (смета не найдена) — ожидаемая ситуация, не логируем
    if (!String(error.message || "").includes("404")) {
      console.error("Error fetching project estimate data:", error);
    }
    throw error;
  }
};

// Получить данные о строительном проектировании для проекта (один GET, детали секций из construction_design_detailed)
export const getConstructionDesign = async (projectId) => {
  try {
    const data = await getProjectEstimate(projectId);

    // Объединяем разделы из construction_design и construction_design_project
    const allSections = [
      ...(data.construction_design || []),
      ...(data.construction_design_project || []),
    ].sort(
      (a, b) =>
        (a.order_index ?? Number.MAX_SAFE_INTEGER) -
        (b.order_index ?? Number.MAX_SAFE_INTEGER),
    );

    // Детали секций из одного ответа (без GET /sections/{id})
    const section_details = buildSectionDetailsFromDetailed(
      data.construction_design_detailed,
    );
    allSections.forEach((section) => {
      const sectionId = section.section?.id;
      if (sectionId == null || !isUnifiedSectionName(section.section?.name)) {
        return;
      }
      const fromRow = pickUnifiedDayEfforts(section.day_efforts);
      const existing = section_details[sectionId];
      if (existing) {
        section_details[sectionId] = {
          ...existing,
          section_name: section.section?.name,
          total_day_efforts:
            pickUnifiedDayEfforts({
              direct: existing.total_day_efforts,
              total: fromRow,
            }) ??
            fromRow ??
            existing.total_day_efforts ??
            0,
        };
      } else {
        section_details[sectionId] = {
          bonus_percent: section.bonus_percent ?? 0,
          average_salary: section.average_salary ?? 0,
          block_expenditures: [],
          section_id: sectionId,
          section_name: section.section?.name,
          total_day_efforts: fromRow ?? 0,
        };
      }
    });

    // Преобразуем данные в формат, ожидаемый компонентами
    return {
      // Основные данные - объединенные разделы
      sections: allSections,
      // Детали по каждой секции (bonus_percent, average_salary, block_expenditures)
      section_details,

      // Новые ключи API
      contract_value: safeNumber(data.contract_value, 0),
      contract_value_before_nds: safeNumber(data.contract_value_before_nds, 0),
      contract_value_construction: safeNumber(
        data.contract_value_construction,
        0,
      ),
      contract_value_construction_percent: safeNumber(
        data.contract_value_construction_percent,
        0,
      ),
      cost_price: safeNumber(data.cost_price, 0),
      difference: safeNumber(data.difference, 0),
      total_area: safeNumber(data.total_area, 0),

      // Блоки/вкладки
      bim_coordination: data.bim_coordination || {},
      quality_control: data.quality_control || {},
      project_delivery: data.project_delivery || {},
      project_management: data.project_management || {},

      // Итоги/проценты
      nds_value: safeNumber(data.nds_value, 0),
      indirect_expenditures: safeNumber(data.indirect_expenditures, 0),
      indirect_expenditures_percent: safeNumber(
        data.indirect_expenditures_percent,
        0,
      ),
      cost_price_percent: safeNumber(data.cost_price_percent, 0),
      pure_income: safeNumber(data.pure_income, 0),
      pure_income_percent: safeNumber(data.pure_income_percent, 0),
      net_profit: safeNumber(data.net_profit, 0),
      net_profit_percent: safeNumber(data.net_profit_percent, 0),
      total_construction_design: data.total_construction_design || {},
      bonus_percent_construction_design: safeNumber(
        data.bonus_percent_construction_design,
        0,
      ),
      total_direct_construction_design: safeNumber(
        data.total_direct_construction_design,
        0,
      ),
      bonus_percent_aup_aho: safeNumber(data.bonus_percent_aup_aho, 0),
      bonus_aup_aho: safeNumber(data.bonus_aup_aho, 0),
      include_bonuses: data.include_bonuses ?? true,

      // Legacy-ключи (для обратной совместимости старых компонентов)
      consent_price: safeNumber(data.contract_value, 0),
      consent_price_before_nds: safeNumber(data.contract_value_before_nds, 0),
      consent_price_construction: safeNumber(
        data.contract_value_construction,
        0,
      ),
      cost_value_construction: safeNumber(data.contract_value_construction, 0),
    };
  } catch (error) {
    console.error("Error fetching construction design data:", error);
    throw error;
  }
};

// Получить данные секции строительного проектирования
export const getSectionConstructionDesign = async (
  projectId,
  projectSectionId,
) => {
  try {
    const response = await fetch(
      `${getAPIURL()}project_estimates/projects/${projectId}/sections/${projectSectionId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error("Error fetching section construction design data:", error);
    throw error;
  }
};

// Обновить данные строительного проектирования для конкретной секции
export const updateSectionConstructionDesign = async (
  projectId,
  projectSectionId,
  data,
) => {
  try {
    const response = await fetch(
      `${getAPIURL()}project_estimates/projects/${projectId}/sections/${projectSectionId}/construction_design`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(data),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        // Если не удалось получить JSON, используем текст
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating section construction design data:", error);
    throw error;
  }
};

// Пользовательский ввод трудозатрат (Норм контроль / Администрирование).
// Калькуляция часто возвращает 0 по этим полям — их нельзя брать из ответа API.
const isPresentEffortValue = (value) =>
  value !== undefined && value !== null && value !== "";

export const toEffortNumber = (value) => {
  if (!isPresentEffortValue(value)) return undefined;
  const n = typeof value === "number" ? value : parseFloat(value);
  return Number.isFinite(n) ? n : undefined;
};

let lastKnownUserEfforts = {
  normative_control_efforts: undefined,
  administration_efforts: undefined,
};

export const rememberUserEfforts = (patch = {}) => {
  if (Object.prototype.hasOwnProperty.call(patch, "normative_control_efforts")) {
    const n = toEffortNumber(patch.normative_control_efforts);
    // 0 с калькуляции нельзя запоминать — это затирает пользовательский ввод
    if (n !== undefined && n !== 0) {
      lastKnownUserEfforts.normative_control_efforts = n;
    }
  }
  if (Object.prototype.hasOwnProperty.call(patch, "administration_efforts")) {
    const n = toEffortNumber(patch.administration_efforts);
    if (n !== undefined && n !== 0) {
      lastKnownUserEfforts.administration_efforts = n;
    }
  }
};

export const getRememberedUserEfforts = () => ({ ...lastKnownUserEfforts });

export const resetRememberedUserEfforts = () => {
  lastKnownUserEfforts = {
    normative_control_efforts: undefined,
    administration_efforts: undefined,
  };
};

const mergeEffortValue = (incoming, remembered) => {
  const incomingNum = toEffortNumber(incoming);
  if (incomingNum !== undefined && incomingNum !== 0) return incomingNum;
  if (remembered !== undefined) return remembered;
  return incomingNum;
};

export const resolveEffortValue = (...candidates) => {
  let firstDefined;
  for (const candidate of candidates) {
    const n = toEffortNumber(candidate);
    if (n === undefined) continue;
    if (firstDefined === undefined) firstDefined = n;
    if (n !== 0) return n;
  }
  return firstDefined ?? 0;
};

export const applyRememberedUserEfforts = (estimate) => {
  if (!estimate) return estimate;
  const n = resolveEffortValue(
    estimate.quality_control?.normative_control_efforts,
    findNormControlDirectEfforts(estimate.quality_control),
    getRememberedUserEfforts().normative_control_efforts,
  );
  const a = resolveEffortValue(
    estimate.project_management?.administration_efforts,
    findAdministrationDirectEfforts(estimate.project_management),
    getRememberedUserEfforts().administration_efforts,
  );
  if (n !== undefined && n !== 0) {
    rememberUserEfforts({ normative_control_efforts: n });
  }
  if (a !== undefined && a !== 0) {
    rememberUserEfforts({ administration_efforts: a });
  }
  const currentN = estimate.quality_control?.normative_control_efforts;
  const currentA = estimate.project_management?.administration_efforts;
  if (currentN === n && currentA === a) {
    return estimate;
  }
  return {
    ...estimate,
    quality_control: {
      ...(estimate.quality_control || {}),
      ...(n !== undefined ? { normative_control_efforts: n } : {}),
    },
    project_management: {
      ...(estimate.project_management || {}),
      ...(a !== undefined ? { administration_efforts: a } : {}),
    },
  };
};

export const rememberUserEffortsFromEstimate = (estimate) => {
  rememberUserEfforts({
    normative_control_efforts: resolveEffortValue(
      estimate?.quality_control?.normative_control_efforts,
      findNormControlDirectEfforts(estimate?.quality_control),
    ),
    administration_efforts: resolveEffortValue(
      estimate?.project_management?.administration_efforts,
      findAdministrationDirectEfforts(estimate?.project_management),
    ),
  });
};

const findNormControlDirectEfforts = (qualityControl) =>
  qualityControl?.roles?.find(
    (role) => role.role === "Норм Контроль" || role.role === "Норм.контроль",
  )?.day_efforts?.direct;

const findAdministrationDirectEfforts = (projectManagement) =>
  projectManagement?.roles?.find(
    (role) => role.role === "Администрирование Проекта",
  )?.day_efforts?.direct;

export const toApiContractSections = (contractSections) => {
  if (!contractSections) return [];
  const sectionsArray = Array.isArray(contractSections.sections)
    ? contractSections.sections
    : Array.isArray(contractSections)
      ? contractSections
      : [];
  return sectionsArray
    .map((cs) => ({
      section_id: cs.section?.id ?? cs.section_id,
      amount: cs.amount || 0,
    }))
    .filter((cs) => cs.section_id != null);
};

// Функция для формирования payload калькуляции согласно новому API
const buildCalculationPayload = async (
  projectId,
  estimateData,
  sectionsData,
) => {
  // Получаем детали для всех секций
  const allSectionDetails = sectionsData || {};

  // Вспомогательная функция для безопасного получения числового значения
  const getNumericValue = (value, defaultValue = 0) => {
    if (typeof value === "number" && !isNaN(value)) {
      return value;
    }
    if (typeof value === "string" && !isNaN(parseFloat(value))) {
      return parseFloat(value);
    }
    return defaultValue;
  };

  // Вспомогательная функция для проверки, является ли раздел единым (без блоков)
  const isUnifiedSection = (sectionName) => {
    const name = sectionName?.toLowerCase().trim();
    return name === "инсоляция" || name === "гп";
  };

  // Порядок разделов: объединённый список, отсортированный по order_index
  const orderedSections = [
    ...(estimateData.construction_design || []),
    ...(estimateData.construction_design_project || []),
  ].sort(
    (a, b) =>
      (a.order_index ?? Number.MAX_SAFE_INTEGER) -
      (b.order_index ?? Number.MAX_SAFE_INTEGER),
  );

  const regularSections = [];
  const unifiedSections = [];
  // Один section_id — одна строка. Иначе раздел из construction_design и
  // construction_design_project уходит в save дважды или теряется при дедупе.
  const seenSectionIds = new Set();

  // Бэкенд при save требует threshold_percent > 0 и average_salary > 0
  const ensurePositive = (val, fallback) =>
    val != null && Number(val) > 0 ? Number(val) : fallback;

  orderedSections.forEach((sectionFromEstimate, order_index) => {
    const sectionId = Number(
      sectionFromEstimate?.section?.id ?? sectionFromEstimate?.section_id,
    );
    if (!Number.isFinite(sectionId) || seenSectionIds.has(sectionId)) return;
    seenSectionIds.add(sectionId);

    const sectionDetails = allSectionDetails[sectionId];
    const sectionName =
      sectionFromEstimate?.section?.name || sectionDetails?.section_name;

    if (isUnifiedSection(sectionName)) {
      const rawThreshold =
        sectionDetails?.threshold_percent ||
        sectionFromEstimate?.expenditures_percent?.threshold;
      const rawSalary = sectionDetails?.average_salary;
      unifiedSections.push({
        section_id: sectionId,
        threshold_percent: ensurePositive(rawThreshold, 0.01),
        average_salary: ensurePositive(rawSalary, 1),
        day_efforts:
          pickUnifiedDayEfforts({
            direct: sectionDetails?.total_day_efforts,
            total: pickUnifiedDayEfforts(sectionFromEstimate?.day_efforts),
          }) ?? 0,
        order_index: unifiedSections.length,
      });
    } else {
      const blocks = (sectionDetails?.block_expenditures || []).map(
        (block) => ({
          tei_id: block.block?.tei_id || 0,
          day_efforts: block.day_efforts || 0,
        }),
      );
      const rawThreshold =
        sectionDetails?.threshold_percent ||
        sectionFromEstimate?.expenditures_percent?.threshold;
      const rawSalary = sectionDetails?.average_salary;
      const result = {
        section_id: sectionId,
        threshold_percent: ensurePositive(rawThreshold, 0.01),
        average_salary: ensurePositive(rawSalary, 1),
        blocks,
        order_index,
      };
      if (
        (rawThreshold == null || Number(rawThreshold) <= 0) &&
        (rawSalary == null || Number(rawSalary) <= 0) &&
        blocks.length === 0
      ) {
        console.warn(
          `[buildCalculationPayload] ⚠️ Раздел ${sectionId} имеет нулевые значения и может не сохраниться`,
        );
      }
      regularSections.push(result);
    }
  });

  const constructionDesign = regularSections;
  const constructionDesignProject = unifiedSections;

  // Приоритет у согласованного значения из секций (оно обновляется после калькуляции/редактирования),
  // иначе — estimateData (при загрузке контекст может ещё не иметь актуального bonus_percent_construction_design)
  const bonusValues = Object.values(allSectionDetails || {})
    .map((det) => det.bonus_percent)
    .filter((val) => val !== undefined && val !== null);

  // Берём первое ненулевое значение из секций, если все значения одинаковые
  // Если значения разные - используем первое ненулевое или fallback на estimateData
  const allSameBonus =
    bonusValues.length > 0 && bonusValues.every((v) => v === bonusValues[0]);
  const firstNonZeroBonus = bonusValues.find((v) => v > 0);
  const fromSections = allSameBonus
    ? bonusValues[0]
    : firstNonZeroBonus !== undefined
      ? firstNonZeroBonus
      : undefined;

  // Определяем глобальный процент бонусов:
  // 1. Из секций (если есть согласованное значение)
  // 2. Из estimateData.bonus_percent_construction_design (fallback)
  // 3. 0 по умолчанию (если ничего не найдено)
  const globalBonusPercent =
    fromSections !== undefined
      ? Number(fromSections)
      : estimateData.bonus_percent_construction_design !== undefined &&
          estimateData.bonus_percent_construction_design !== null
        ? Number(estimateData.bonus_percent_construction_design)
        : getNumericValue(estimateData.bonus_percent_construction_design, 0);

  // section_id: приоритет у role.section_id (GET/ответ API теперь везде отдаёт section_id), иначе — по имени раздела
  const sectionsByName = new Map();
  const addSections = (arr) => {
    (arr || []).forEach((item) => {
      const id = item?.section?.id;
      const name = item?.section?.name?.trim();
      if (id != null && name != null) sectionsByName.set(name, id);
    });
  };
  addSections(estimateData.construction_design);
  addSections(estimateData.construction_design_project);
  const bimSectionId = (role) => resolveBimRoleSectionId(role, sectionsByName);
  const qualitySectionId = (role) => {
    if (role.section_id != null) return role.section_id;
    const n = role.role?.trim();
    if (!n || n === "РО 5" || n === "Норм Контроль" || n === "Норм.контроль")
      return null;
    return sectionsByName.get(n) ?? null;
  };
  const deliverySectionId = (role) =>
    role.section_id != null
      ? role.section_id
      : (sectionsByName.get(role.role?.trim()) ?? null);

  // Формируем блоки для калькуляции
  const payload = {
    include_bonuses: estimateData.include_bonuses ?? true,
    bonus_percent_construction_design: globalBonusPercent,
    bonus_percent_aup_aho: getNumericValue(
      estimateData.bonus_percent_aup_aho,
      0,
    ),
    construction_design: constructionDesign,
    construction_design_project: constructionDesignProject, // Всегда включаем, даже если массив пустой (бэкенд требует это поле)
    bim_coordination: {
      include_bonuses: estimateData.bim_coordination?.include_bonuses ?? true,
      quality_control_percent:
        estimateData.bim_coordination?.quality_control_percent || 0,
      direct_bonus_percent:
        estimateData.bim_coordination?.direct_bonus_percent || 0,
      bim_role_salaries: firstNonEmptyList(
        estimateData.bim_coordination?.bim_role_salaries,
        estimateData.bim_coordination?.roles,
      ).map((role, index) => ({
        role: role.role,
        average_salary: role.average_salary || 0,
        section_id: bimSectionId(role),
        order_index: index,
      })),
    },
    quality_control: {
      include_bonuses: estimateData.quality_control?.include_bonuses ?? true,
      quality_control_percent:
        estimateData.quality_control?.quality_control_percent || 0,
      direct_bonus_percent:
        estimateData.quality_control?.direct_bonus_percent || 0,
      normative_control_efforts: resolveEffortValue(
        estimateData.quality_control?.normative_control_efforts,
        getRememberedUserEfforts().normative_control_efforts,
        findNormControlDirectEfforts(estimateData.quality_control),
      ),
      vk_control_efforts: estimateData.quality_control?.vk_control_efforts || 0,
      quality_control_role_salaries: firstNonEmptyList(
        estimateData.quality_control?.quality_control_role_salaries,
        estimateData.quality_control?.roles,
      ).map((role, index) => ({
        role: role.role,
        average_salary: role.average_salary || 0,
        section_id: qualitySectionId(role),
        order_index: index,
      })),
    },
    project_delivery: {
      include_bonuses: estimateData.project_delivery?.include_bonuses ?? true,
      average_salary: estimateData.project_delivery?.average_salary || 0,
      quality_control_percent:
        estimateData.project_delivery?.quality_control_percent || 0,
      direct_bonus_percent:
        estimateData.project_delivery?.direct_bonus_percent || 0,
      project_delivery_role_salaries: firstNonEmptyList(
        estimateData.project_delivery?.project_delivery_role_salaries,
        estimateData.project_delivery?.roles,
      ).map((role, index) => ({
        role: role.role,
        average_salary: role.average_salary || 0,
        section_id: deliverySectionId(role),
        order_index: index,
      })),
    },
    project_management: {
      include_bonuses: estimateData.project_management?.include_bonuses ?? true,
      quality_control_percent:
        estimateData.project_management?.quality_control_percent || 0,
      direct_bonus_percent:
        estimateData.project_management?.direct_bonus_percent || 0,
      administration_efforts: resolveEffortValue(
        estimateData.project_management?.administration_efforts,
        getRememberedUserEfforts().administration_efforts,
        findAdministrationDirectEfforts(estimateData.project_management),
      ),
      management_role_salaries: (
        estimateData.project_management?.roles || []
      ).map((role, index) => ({
        role: role.role,
        average_salary: role.average_salary || 0,
        coefficient: role.coefficient || 0,
        order_index: index,
      })),
    },
  };

  const contractSectionsPayload = toApiContractSections(
    estimateData.contract_sections,
  );
  // Пустой массив и save, и calculate воспринимают как «заменить список на пустой».
  if (contractSectionsPayload.length > 0) {
    payload.contract_sections = contractSectionsPayload;
  }

  return payload;
};

// Функция для обновления контекста с результатами калькуляции
export const updateContextWithCalculationResults = (
  context,
  calculationResults,
) => {
  if (!calculationResults) return context;

  console.log("[updateContextWithCalculationResults] Входные данные:", {
    context_sections_keys: Object.keys(context.sections || {}),
    context_sections_27: context.sections?.[27],
    calculationResults_construction_design_detailed:
      calculationResults.construction_design_detailed?.find(
        (d) => d.section_id === 27,
      ),
  });

  // Обновляем данные сметы результатами калькуляции
  const updatedEstimate = {
    ...context.estimate,
    ...calculationResults,
  };

  // Обновляем construction_design с новыми данными из construction_design_detailed
  if (calculationResults.construction_design_detailed) {
    const updatedConstructionDesign =
      updatedEstimate.construction_design?.map((section) => {
        if (isUnifiedSectionName(section.section?.name)) {
          return section;
        }
        const detailedSection =
          calculationResults.construction_design_detailed.find(
            (det) => det.section_id === section.section.id,
          );

        if (detailedSection) {
          // Обновляем day_efforts на основе данных из block_expenditures
          const totalDirectEfforts =
            detailedSection.block_expenditures?.reduce(
              (sum, block) => sum + (block.day_efforts || 0),
              0,
            ) || 0;
          const totalBonusEfforts =
            detailedSection.block_expenditures?.reduce(
              (sum, block) => sum + (block.bonus_day_efforts || 0),
              0,
            ) || 0;

          return {
            ...section,
            day_efforts: {
              direct: totalDirectEfforts,
              bonus: totalBonusEfforts,
              total: totalDirectEfforts + totalBonusEfforts,
            },
          };
        }

        return section;
      }) || [];

    updatedEstimate.construction_design = updatedConstructionDesign;
  }

  // Обновляем construction_design_project с новыми данными из результатов калькуляции
  if (calculationResults.construction_design_project) {
    updatedEstimate.construction_design_project =
      calculationResults.construction_design_project;
  }

  // Объединяем разделы для обратной совместимости, избегая дубликатов.
  // Для единых разделов приоритет у construction_design_project: там day_efforts пользователя.
  const projectById = new Map(
    (updatedEstimate.construction_design_project || [])
      .filter((s) => s.section?.id != null)
      .map((s) => [s.section.id, s]),
  );
  const mergedConstructionDesign = (
    updatedEstimate.construction_design || []
  ).map((section) => {
    const fromProject = projectById.get(section.section?.id);
    if (!fromProject || !isUnifiedSectionName(section.section?.name)) {
      return section;
    }
    const fromProjectEfforts = pickUnifiedDayEfforts(fromProject.day_efforts);
    const existingEfforts = pickUnifiedDayEfforts(section.day_efforts);
    const direct =
      fromProjectEfforts ??
      existingEfforts ??
      fromProject.day_efforts?.direct ??
      section.day_efforts?.direct ??
      0;
    const bonus =
      fromProject.day_efforts?.bonus ?? section.day_efforts?.bonus ?? 0;
    return {
      ...section,
      ...fromProject,
      day_efforts: {
        direct,
        bonus,
        total: fromProject.day_efforts?.total ?? direct + bonus,
      },
      money_expenditures:
        fromProject.money_expenditures ?? section.money_expenditures,
    };
  });
  const existingSectionIds = new Set(
    mergedConstructionDesign.map((s) => s.section?.id).filter(Boolean),
  );
  const projectSections = (
    updatedEstimate.construction_design_project || []
  ).filter((s) => {
    const sectionId = s.section?.id;
    return sectionId && !existingSectionIds.has(sectionId);
  });
  updatedEstimate.construction_design = [
    ...mergedConstructionDesign,
    ...projectSections,
  ];

  // Ответ /calculate иногда короче сметы, которая была на экране.
  // Сохранение потом записывает этот список целиком, и пропущенный раздел
  // пропадает с сервера. Возвращаем такие разделы из сметы до калькуляции.
  const keptSectionIds = new Set(
    (updatedEstimate.construction_design || [])
      .map((section) => section?.section?.id)
      .filter((id) => id != null),
  );
  const restoredSections = [];
  const seenRestoredIds = new Set();
  [
    ...(context.estimate?.construction_design || []),
    ...(context.estimate?.construction_design_project || []),
  ].forEach((section) => {
    const sectionId = section?.section?.id;
    if (sectionId == null || keptSectionIds.has(sectionId)) return;
    if (seenRestoredIds.has(sectionId)) return;
    seenRestoredIds.add(sectionId);
    restoredSections.push(section);
  });
  if (restoredSections.length > 0) {
    console.warn(
      "[updateContextWithCalculationResults] Ответ калькуляции не содержит разделы, оставляем их в смете:",
      restoredSections.map((section) => ({
        id: section?.section?.id,
        name: section?.section?.name,
      })),
    );
    updatedEstimate.construction_design = [
      ...updatedEstimate.construction_design,
      ...restoredSections,
    ];
  }

  // contract_sections — пользовательский ввод, а не результат калькуляции.
  // Ответ /calculate часто возвращает разделы с сервера (до сохранения),
  // из-за чего локальное удаление/добавление сразу откатывалось.
  if (context.estimate && "contract_sections" in context.estimate) {
    updatedEstimate.contract_sections = context.estimate.contract_sections;
  } else {
    delete updatedEstimate.contract_sections;
  }

  // Список ролей — пользовательский ввод. /calculate часто возвращает только дефолтные колонки.
  const contextBimRoles = firstNonEmptyList(
    context.estimate?.bim_coordination?.roles,
    context.estimate?.bim_coordination?.bim_role_salaries,
  );
  const apiBimRoles = firstNonEmptyList(
    calculationResults.bim_coordination?.roles,
    calculationResults.bim_coordination?.bim_role_salaries,
  );
  updatedEstimate.bim_coordination = {
    ...(updatedEstimate.bim_coordination || {}),
    roles: mergeCalculatedModuleRoles(contextBimRoles, apiBimRoles),
  };

  const contextQualityRoles = firstNonEmptyList(
    context.estimate?.quality_control?.roles,
    context.estimate?.quality_control?.quality_control_role_salaries,
  );
  const apiQualityRoles = firstNonEmptyList(
    calculationResults.quality_control?.roles,
    calculationResults.quality_control?.quality_control_role_salaries,
  );
  updatedEstimate.quality_control = {
    ...(updatedEstimate.quality_control || {}),
    roles: mergeCalculatedModuleRoles(contextQualityRoles, apiQualityRoles),
  };

  const contextPmRoles = firstNonEmptyList(
    context.estimate?.project_management?.roles,
    context.estimate?.project_management?.management_role_salaries,
  );
  const apiPmRoles = firstNonEmptyList(
    calculationResults.project_management?.roles,
    calculationResults.project_management?.management_role_salaries,
  );
  updatedEstimate.project_management = {
    ...(updatedEstimate.project_management || {}),
    roles: mergeCalculatedModuleRoles(contextPmRoles, apiPmRoles),
  };

  // Эти поля — пользовательский ввод, а не результат калькуляции.
  // Ответ API часто возвращает 0; всегда восстанавливаем последнее известное значение.
  const preservedEstimate = applyRememberedUserEfforts({
    ...updatedEstimate,
    quality_control: {
      ...(updatedEstimate.quality_control || {}),
      normative_control_efforts: mergeEffortValue(
        context.estimate?.quality_control?.normative_control_efforts,
        getRememberedUserEfforts().normative_control_efforts,
      ),
    },
    project_management: {
      ...(updatedEstimate.project_management || {}),
      administration_efforts: mergeEffortValue(
        context.estimate?.project_management?.administration_efforts,
        getRememberedUserEfforts().administration_efforts,
      ),
    },
  });
  updatedEstimate.quality_control = preservedEstimate.quality_control;
  updatedEstimate.project_management = preservedEstimate.project_management;

  // Обновляем детали секций с новыми данными из construction_design_detailed
  const updatedSections = { ...context.sections };

  if (calculationResults.construction_design_detailed) {
    calculationResults.construction_design_detailed.forEach((sectionDetail) => {
      const sectionId = sectionDetail.section_id;
      const existingSection = updatedSections[sectionId];

      // Сохраняем average_salary из текущего контекста, если в ответе калькуляции оно 0 или отсутствует
      // Это важно для единых разделов (Инсоляция, ГП), где average_salary вводится пользователем
      const preservedAverageSalary =
        sectionDetail.average_salary || existingSection?.average_salary || 0;
      // Аналогично сохраняем section_name
      const preservedSectionName = existingSection?.section_name;
      // Сохраняем bonus_percent из текущего контекста, если API не вернул его
      // API может не возвращать bonus_percent в construction_design_detailed
      const preservedBonusPercent =
        sectionDetail.bonus_percent !== undefined &&
        sectionDetail.bonus_percent !== null
          ? sectionDetail.bonus_percent
          : (existingSection?.bonus_percent ?? 0);

      const mappedBlocks =
        sectionDetail.block_expenditures?.map((block) => ({
          block: {
            id: block.block?.id || block.block?.tei_id,
            tei_id: block.block?.tei_id,
            name: block.block?.name || `Block ${block.block?.tei_id}`,
          },
          floor_count: block.floor_count || 0,
          day_efforts: block.day_efforts || 0,
          bonus_day_efforts: block.bonus_day_efforts || 0,
          money_expenditures: block.money_expenditures || 0,
          bonus_money_expenditures: block.bonus_money_expenditures || 0,
        })) || [];
      const sortedBlocks = sortBlockExpendituresOrder(mappedBlocks);
      const existingBlocks = existingSection?.block_expenditures;
      const blockExpenditures =
        sortedBlocks.length > 0
          ? sortedBlocks
          : Array.isArray(existingBlocks) && existingBlocks.length > 0
            ? existingBlocks
            : sortedBlocks;

      if (existingSection) {
        // Обновляем существующие данные секции (блоки: Паркинг → Блок 1,2,… → остальные).
        // Пустой список блоков в ответе не затирает уже введённые трудозатраты.
        updatedSections[sectionId] = {
          ...existingSection,
          bonus_percent: preservedBonusPercent,
          average_salary: preservedAverageSalary,
          section_name: preservedSectionName,
          block_expenditures: blockExpenditures,
        };
      } else {
        // Создаем новые данные секции, если их не было
        updatedSections[sectionId] = {
          bonus_percent: preservedBonusPercent,
          average_salary: preservedAverageSalary,
          section_name: preservedSectionName,
          block_expenditures: sortedBlocks,
        };
      }
    });
  }

  // Обновляем детали секций для единых разделов из construction_design_project
  // Для единых разделов данные уже должны быть загружены через getSectionConstructionDesign
  // Здесь обновляем total_day_efforts для Инсоляции и ГП, если нужно
  if (calculationResults.construction_design_project) {
    calculationResults.construction_design_project.forEach((section) => {
      const sectionId = section.section?.id;
      if (!sectionId) return;

      // Находим название раздела для определения типа
      const sectionName = section.section?.name?.toLowerCase().trim();
      const isUnified =
        sectionName === "инсоляция" || sectionName === "гп";

      if (updatedSections[sectionId] && isUnified) {
        const fromCalc = pickUnifiedDayEfforts(section.day_efforts);
        const existing = updatedSections[sectionId].total_day_efforts;
        updatedSections[sectionId] = {
          ...updatedSections[sectionId],
          total_day_efforts:
            pickUnifiedDayEfforts({
              direct: fromCalc,
              total: existing,
            }) ??
            existing ??
            0,
        };
      }
    });
  }

  console.log("[updateContextWithCalculationResults] Результат:", {
    updatedSections_27: updatedSections[27],
  });

  const result = {
    ...context,
    estimate: updatedEstimate,
    sections: updatedSections,
  };

  return result;
};

// Функция для сохранения с обновлением контекста
export const saveAndUpdateContext = async (
  projectId,
  estimateData,
  sectionsData,
) => {
  try {
    // Сохраняем данные
    const saveResult = await saveConstructionDesignPreservingData(
      projectId,
      estimateData,
      sectionsData,
    );

    // Получаем обновленные данные после сохранения
    const updatedContext = await createEstimateContext(projectId);

    // СТАБИЛЬНЫЙ ПОРЯДОК РАЗДЕЛОВ:
    // После сохранения бэкенд может вернуть construction_design в другом порядке.
    // Чтобы список разделов в WorkTab не «перепрыгивал», приводим порядок
    // к тому, который был до сохранения (по ID секций).
    try {
      const beforeSections = estimateData?.construction_design || [];
      const afterSections = updatedContext.estimate?.construction_design || [];

      if (beforeSections.length && afterSections.length) {
        const orderMap = {};
        beforeSections.forEach((section, index) => {
          const id = section?.section?.id;
          if (id !== undefined && id !== null && orderMap[id] === undefined) {
            orderMap[id] = index;
          }
        });

        const sortedAfter = [...afterSections].sort((a, b) => {
          const idA = a?.section?.id;
          const idB = b?.section?.id;
          const orderA =
            idA !== undefined && idA !== null && orderMap[idA] !== undefined
              ? orderMap[idA]
              : Number.MAX_SAFE_INTEGER;
          const orderB =
            idB !== undefined && idB !== null && orderMap[idB] !== undefined
              ? orderMap[idB]
              : Number.MAX_SAFE_INTEGER;
          return orderA - orderB;
        });

        updatedContext.estimate = {
          ...updatedContext.estimate,
          construction_design: sortedAfter,
        };
      }
    } catch (orderError) {
      console.warn(
        "[saveAndUpdateContext] Не удалось стабилизировать порядок разделов:",
        orderError,
      );
    }

    // Сравниваем разделы до и после сохранения.
    // Оба списка: обычные и единые (ГП, Инсоляция). Id приводим к числу,
    // иначе "23" и 23 считаются разными и успешное сохранение выглядит как потеря.
    const sectionRowsBefore = collectEstimateSectionRows(estimateData);
    const sectionIdsAfter = new Set(
      collectEstimateSectionRows(updatedContext.estimate)
        .map(readSectionRowId)
        .filter((id) => id != null),
    );
    const sectionIdsBefore = new Set(
      sectionRowsBefore.map(readSectionRowId).filter((id) => id != null),
    );
    const missingSections = Array.from(sectionIdsBefore).filter(
      (id) => !sectionIdsAfter.has(id),
    );
    if (missingSections.length > 0) {
      const labels = missingSections
        .map((id) => {
          const row = sectionRowsBefore.find(
            (section) => readSectionRowId(section) === id,
          );
          return row?.section?.name || `Раздел #${id}`;
        })
        .join(", ");
      console.error(
        "[saveAndUpdateContext] Потеряны разделы после сохранения:",
        missingSections,
      );
      throw sectionsLostAfterWriteError(labels);
    } else {
      console.log(
        "[saveAndUpdateContext] ✅ Все разделы сохранены успешно. Было:",
        sectionIdsBefore.size,
        "Стало:",
        sectionIdsAfter.size,
      );
    }

    const savedContractSections = toApiContractSections(
      estimateData?.contract_sections,
    );
    const loadedContractSections = toApiContractSections(
      updatedContext.estimate?.contract_sections,
    );
    if (
      savedContractSections.length > 0 &&
      loadedContractSections.length === 0
    ) {
      console.warn(
        "[saveAndUpdateContext] GET после save вернул пустые contract_sections. Отправляли:",
        savedContractSections,
      );
    }

    return {
      saveResult,
      context: updatedContext,
    };
  } catch (error) {
    console.error("Error saving and updating context:", error);
    throw error;
  }
};

// Рассчитать строительное проектирование (обновленная версия)
export const calculateConstructionDesign = async (
  projectId,
  estimateData,
  sectionsData,
) => {
  try {
    // Если данные не переданы, получаем их из API
    if (!estimateData) {
      const context = await createEstimateContext(projectId);
      estimateData = context.estimate;
      sectionsData = context.sections;
    }

    const protectedData = await protectEstimateSectionsForWrite(
      projectId,
      estimateData,
      sectionsData,
    );
    estimateData = protectedData.estimateData;
    sectionsData = protectedData.sectionsData;

    // Формируем payload согласно новому API
    const payload = await buildCalculationPayload(
      projectId,
      estimateData,
      sectionsData,
    );

    const response = await fetch(
      `${getAPIURL()}project_estimates/projects/${projectId}/calculate`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(payload),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    const result = keepSentSectionsInCalculationResult(
      await response.json(),
      estimateData,
      sectionsData,
    );

    // Возвращаем полный результат для лайв обновления интерфейса
    return result;
  } catch (error) {
    console.error("Error calculating construction design:", error);
    throw error;
  }
};

// Рассчитать строительное проектирование для конкретной секции (обновленная версия)
export const calculateSectionConstructionDesign = async (
  contextData,
  projectId,
  sectionId,
  sectionData,
  payloadData = null,
) => {
  try {
    // Используем переданный контекст или получаем через API
    const context = contextData || (await createEstimateContext(projectId));
    let estimateData = context.estimate;
    let sectionsData = context.sections;

    // Находим секцию в массивах sections
    // Сначала ищем в construction_design (обычные разделы)
    let section = estimateData.construction_design?.find(
      (section) => section.section.id === sectionId,
    );

    // Если не нашли, ищем в construction_design_project (единые разделы: ГП, Инсоляция)
    if (!section) {
      section = estimateData.construction_design_project?.find(
        (section) => section.section.id === sectionId,
      );
    }

    if (!section) {
      throw new Error(`Секция с ID ${sectionId} не найдена`);
    }

    // Обновляем данные секции в контексте, если переданы новые данные
    if (sectionData) {
      sectionsData[sectionId] = {
        ...sectionsData[sectionId],
        ...sectionData,
      };
      console.log(
        `[calculateSectionConstructionDesign] Обновлены sectionsData для секции ${sectionId}:`,
        {
          sectionId,
          sectionData,
          updated_sectionsData: sectionsData[sectionId],
        },
      );
    }

    // НЕ обновляем estimateData через payloadData, так как payloadData содержит payload для API, а не данные для обновления
    // Это было причиной обнуления значений contract_value, cost_price и т.д.

    console.log(
      `[calculateSectionConstructionDesign] Вызываем buildCalculationPayload для секции ${sectionId}:`,
      {
        sectionId,
        sectionsData_sectionId: sectionsData[sectionId],
        estimateData_construction_design_project:
          estimateData.construction_design_project?.find(
            (s) => s.section?.id === sectionId,
          ),
      },
    );

    const protectedData = await protectEstimateSectionsForWrite(
      projectId,
      estimateData,
      sectionsData,
    );
    estimateData = protectedData.estimateData;
    sectionsData = protectedData.sectionsData;

    // Используем новую функцию для формирования payload
    const payload = await buildCalculationPayload(
      projectId,
      estimateData,
      sectionsData,
    );

    const response = await fetch(
      `${getAPIURL()}project_estimates/projects/${projectId}/calculate`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(payload),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        console.error("Ошибка API калькуляции секции:", errorData);
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          console.error("Ошибка API калькуляции секции (текст):", errorText);
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    const result = keepSentSectionsInCalculationResult(
      await response.json(),
      estimateData,
      sectionsData,
    );

    // Проверяем, пересчитал ли бэкенд money_expenditures (для единых разделов ГП, Инсоляция)
    const gpSection = result?.construction_design_project?.find(
      (s) => s.section?.id === sectionId,
    );
    if (gpSection) {
      const payloadSection = payload.construction_design_project?.find(
        (s) => s.section_id === sectionId,
      );
      const salary = payloadSection?.average_salary || 0;
      const direct =
        gpSection.day_efforts?.direct ?? payloadSection?.day_efforts ?? 0;
      const bonus = gpSection.day_efforts?.bonus ?? 0;
      const expectedDirect =
        (typeof direct === "number" ? direct : (direct?.direct ?? 0)) * salary;
      const expectedBonus =
        (typeof bonus === "number" ? bonus : (bonus?.bonus ?? 0)) * salary;

      if (
        gpSection.money_expenditures?.direct === 0 &&
        gpSection.money_expenditures?.bonus === 0 &&
        expectedDirect > 0
      ) {
        console.warn(
          `[calculateSectionConstructionDesign] Бэкенд не пересчитал money_expenditures для секции ${sectionId}`,
        );
      }
    }

    // Обновляем контекст с результатами калькуляции
    const updatedContext = updateContextWithCalculationResults(
      contextData,
      result,
    );

    return {
      ...result,
      updatedContext: updatedContext,
    };
  } catch (error) {
    console.error("Error calculating section construction design:", error);
    throw error;
  }
};

const collectEstimateSectionRows = (estimate) => [
  ...(estimate?.construction_design || []),
  ...(estimate?.construction_design_project || []),
];

const readSectionRowId = (row) => {
  const id = Number(row?.section?.id ?? row?.section_id);
  return Number.isFinite(id) ? id : null;
};

const readPositiveNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const sectionLabel = (id, nameById) =>
  nameById.get(Number(id)) || `Раздел #${id}`;

/**
 * Локальный список перед save иногда короче серверного: вкладка подставляет
 * свой state и затирает construction_design_project. Save заменяет состав
 * целиком, поэтому отсутствующий в запросе раздел пропадает со сметы.
 * Здесь возвращаем такие разделы из свежего GET и подставляем блоки/ставку,
 * если локальные детали пустые.
 */
const mergeServerSectionsIntoSave = (
  estimateData,
  sectionsData,
  serverEstimate,
  excludeIds = new Set(),
) => {
  const localRows = collectEstimateSectionRows(estimateData);
  const localIds = new Set(
    localRows.map(readSectionRowId).filter((id) => id != null),
  );
  const serverRows = collectEstimateSectionRows(serverEstimate);
  const serverDetails = buildSectionDetailsFromDetailed(
    serverEstimate?.construction_design_detailed,
  );
  const nameById = new Map();
  const rememberName = (id, name) => {
    if (id == null || !name || nameById.has(id)) return;
    nameById.set(id, name);
  };

  serverRows.forEach((row) => {
    rememberName(readSectionRowId(row), row?.section?.name);
  });
  localRows.forEach((row) => {
    rememberName(readSectionRowId(row), row?.section?.name);
  });

  const seenServerIds = new Set();
  const missingRows = [];
  serverRows.forEach((row) => {
    const id = readSectionRowId(row);
    if (id == null || seenServerIds.has(id)) return;
    seenServerIds.add(id);
    if (excludeIds.has(id)) return;
    if (!localIds.has(id)) missingRows.push(row);
  });

  const nextSections = { ...(sectionsData || {}) };
  const restoredBlockSectionIds = [];

  const applyServerDetail = (id, localDetail, serverRow) => {
    const serverDetail = serverDetails[id];
    const localBlocks = Array.isArray(localDetail?.block_expenditures)
      ? localDetail.block_expenditures
      : [];
    const serverBlocks = Array.isArray(serverDetail?.block_expenditures)
      ? serverDetail.block_expenditures
      : [];
    if (localBlocks.length === 0 && serverBlocks.length > 0) {
      restoredBlockSectionIds.push(id);
    }
    nextSections[id] = {
      bonus_percent:
        localDetail?.bonus_percent ?? serverDetail?.bonus_percent ?? 0,
      ...(serverDetail || {}),
      ...(localDetail || {}),
      average_salary:
        readPositiveNumber(localDetail?.average_salary) ??
        readPositiveNumber(serverDetail?.average_salary) ??
        readPositiveNumber(serverRow?.average_salary) ??
        localDetail?.average_salary ??
        0,
      threshold_percent:
        readPositiveNumber(localDetail?.threshold_percent) ??
        readPositiveNumber(serverRow?.expenditures_percent?.threshold) ??
        readPositiveNumber(serverDetail?.threshold_percent) ??
        0,
      block_expenditures: localBlocks.length > 0 ? localBlocks : serverBlocks,
      section_id: id,
      section_name:
        localDetail?.section_name ||
        serverRow?.section?.name ||
        serverDetail?.section_name ||
        nameById.get(id),
      total_day_efforts:
        pickUnifiedDayEfforts({
          direct: localDetail?.total_day_efforts,
          total: pickUnifiedDayEfforts(serverRow?.day_efforts),
        }) ??
        localDetail?.total_day_efforts ??
        pickUnifiedDayEfforts(serverRow?.day_efforts) ??
        0,
    };
  };

  localRows.forEach((row) => {
    const id = readSectionRowId(row);
    if (id == null) return;
    applyServerDetail(id, nextSections[id], row);
  });

  let nextOrder =
    localRows.reduce(
      (max, row) => Math.max(max, Number(row?.order_index) || 0),
      -1,
    ) + 1;
  const restoredRows = missingRows.map((row) => {
    const id = readSectionRowId(row);
    const restored = { ...row, order_index: nextOrder };
    nextOrder += 1;
    applyServerDetail(id, nextSections[id], restored);
    return restored;
  });

  if (restoredRows.length > 0) {
    console.warn(
      "[saveConstructionDesignPreservingData] В локальном списке не было разделов, которые есть на сервере. Возвращаем их в сохранение:",
      restoredRows.map((row) => ({
        id: readSectionRowId(row),
        name: row?.section?.name,
      })),
    );
  }
  if (restoredBlockSectionIds.length > 0) {
    console.warn(
      "[saveConstructionDesignPreservingData] Подставлены блоки с сервера, локальные детали были пустыми:",
      restoredBlockSectionIds,
    );
  }

  return {
    estimateData:
      restoredRows.length > 0
        ? {
            ...estimateData,
            construction_design: [
              ...(estimateData?.construction_design || []),
              ...restoredRows,
            ],
          }
        : estimateData,
    sectionsData: nextSections,
    nameById,
    serverSectionIds: seenServerIds,
  };
};

async function protectEstimateSectionsForWrite(
  projectId,
  estimateData,
  sectionsData,
) {
  rememberEstimateSections(projectId, estimateData);
  try {
    await getProjectEstimate(projectId);
  } catch (err) {
    const message = String(err?.message || "");
    if (!message.includes("404")) {
      console.warn(
        "[protectEstimateSectionsForWrite] Не удалось сверить разделы с сервером:",
        err,
      );
    }
  }
  const remembered = getRememberedEstimate(projectId);
  if (!remembered) {
    return { estimateData, sectionsData };
  }
  // Удалённые в интерфейсе разделы тоже отправляем, пока не вызван DELETE.
  // Иначе calculate сотрёт их на сервере раньше сохранения.
  return mergeServerSectionsIntoSave(
    estimateData,
    sectionsData,
    remembered,
  );
}

function keepSentSectionsInCalculationResult(result, estimateData, sectionsData) {
  if (!result) return result;
  const sentRows = collectEstimateSectionRows(estimateData);
  const returnedIds = new Set(
    collectEstimateSectionRows(result)
      .map(readSectionRowId)
      .filter((id) => id != null),
  );
  const missing = [];
  const seen = new Set();
  sentRows.forEach((row) => {
    const id = readSectionRowId(row);
    if (id == null || returnedIds.has(id) || seen.has(id)) return;
    seen.add(id);
    missing.push(row);
  });
  const detailed = Array.isArray(result.construction_design_detailed)
    ? result.construction_design_detailed.map((detail) => {
        const id = Number(detail?.section_id);
        const localBlocks =
          sectionsData?.[id]?.block_expenditures ||
          sectionsData?.[String(id)]?.block_expenditures;
        const apiBlocks = detail?.block_expenditures;
        if (
          (!Array.isArray(apiBlocks) || apiBlocks.length === 0) &&
          Array.isArray(localBlocks) &&
          localBlocks.length > 0
        ) {
          return { ...detail, block_expenditures: localBlocks };
        }
        return detail;
      })
    : result.construction_design_detailed;
  return {
    ...result,
    ...(missing.length > 0 && {
      construction_design: [...(result.construction_design || []), ...missing],
    }),
    ...(detailed !== result.construction_design_detailed && {
      construction_design_detailed: detailed,
    }),
  };
}

const readPayloadSectionIds = (payload) => {
  const ids = new Set();
  [
    ...(payload?.construction_design || []),
    ...(payload?.construction_design_project || []),
  ].forEach((section) => {
    const id = Number(section?.section_id);
    if (Number.isFinite(id)) ids.add(id);
  });
  return ids;
};

// Сохранить данные строительного проектирования с сохранением существующих значений
// Сохранить данные строительного проектирования (обновленная версия)
export const saveConstructionDesignPreservingData = async (
  projectId,
  estimateData,
  sectionsData,
) => {
  try {
    // Если данные не переданы, получаем их из API
    if (!estimateData) {
      const context = await createEstimateContext(projectId);
      estimateData = context.estimate;
      sectionsData = context.sections;
    }

    rememberEstimateSections(projectId, estimateData);

    let serverEstimate = null;
    try {
      serverEstimate = await getProjectEstimate(projectId);
    } catch (err) {
      const message = String(err?.message || "");
      if (!message.includes("404")) {
        throw new Error(
          `Не удалось сверить разделы сметы перед сохранением (${message}). Сохранение отменено, чтобы не стереть разделы.`,
        );
      }
    }

    let sectionNameById = new Map();
    let serverSectionIds = new Set();
    const pendingDeleted = getPendingDeletedSet(projectId);
    if (serverEstimate) {
      const merged = mergeServerSectionsIntoSave(
        estimateData,
        sectionsData,
        serverEstimate,
        pendingDeleted,
      );
      estimateData = merged.estimateData;
      sectionsData = merged.sectionsData;
      sectionNameById = merged.nameById;
      serverSectionIds = merged.serverSectionIds;
    }
    rememberEstimateSections(projectId, estimateData);
    const remembered = getRememberedEstimate(projectId);
    if (remembered) {
      const merged = mergeServerSectionsIntoSave(
        estimateData,
        sectionsData,
        remembered,
        pendingDeleted,
      );
      estimateData = merged.estimateData;
      sectionsData = merged.sectionsData;
      remembered.construction_design?.forEach((row) => {
        const id = readSectionRowId(row);
        const name = row?.section?.name;
        if (id != null && name && !sectionNameById.has(id)) {
          sectionNameById.set(id, name);
        }
      });
    }

    // Используем ту же функцию для формирования payload, что и для калькуляции
    const payload = await buildCalculationPayload(
      projectId,
      estimateData,
      sectionsData,
    );

    if (serverEstimate) {
      const payloadIds = readPayloadSectionIds(payload);
      const omitted = [...serverSectionIds].filter((id) => !payloadIds.has(id));
      if (omitted.length > 0) {
        const labels = omitted
          .map((id) => sectionLabel(id, sectionNameById))
          .join(", ");
        throw new Error(
          `Сохранение отменено: в запрос не попали разделы ${labels}. Смета не изменена.`,
        );
      }
    }

    const contractSectionsForSave = toApiContractSections(
      estimateData.contract_sections,
    );
    if (contractSectionsForSave.length > 0) {
      payload.contract_sections = contractSectionsForSave;
    } else {
      // Пустой массив бэкенд воспринимает как «заменить список на пустой».
      // Удаление раздела — отдельный DELETE; в save не отправляем wipe.
      delete payload.contract_sections;
    }

    // Логируем для отладки сохранения
    console.log(
      "[saveConstructionDesignPreservingData] estimateData.construction_design:",
      estimateData?.construction_design?.map((s) => ({
        id: s.section?.id,
        name: s.section?.name,
      })),
    );
    console.log(
      "[saveConstructionDesignPreservingData] sectionsData keys:",
      Object.keys(sectionsData || {}),
    );
    console.log(
      "[saveConstructionDesignPreservingData] payload.construction_design:",
      payload?.construction_design?.map((s) => ({
        section_id: s.section_id,
        blocks_count: s.blocks?.length || 0,
        threshold_percent: s.threshold_percent,
        average_salary: s.average_salary,
      })),
    );

    // Используем данные construction_design напрямую из payload
    // Блоки уже правильно сгруппированы по секциям в массиве blocks
    const constructionDesign = payload.construction_design || [];

    // Логируем данные перед отправкой
    console.log(
      "[saveConstructionDesignPreservingData] 📤 Полный payload.construction_design перед отправкой:",
      JSON.stringify(
        constructionDesign.map((s) => ({
          section_id: s.section_id,
          threshold_percent: s.threshold_percent,
          average_salary: s.average_salary,
          blocks_count: s.blocks?.length || 0,
          blocks: s.blocks,
        })),
        null,
        2,
      ),
    );

    // Формируем финальный payload с правильной структурой construction_design
    // где для каждой секции все блоки находятся в одном объекте
    const finalPayload = {
      ...payload,
      construction_design: constructionDesign,
    };

    console.log(
      "[saveConstructionDesignPreservingData] contract_sections:",
      finalPayload.contract_sections ?? "(omit, не затираем на сервере)",
    );

    const response = await fetch(
      `${getAPIURL()}project_estimates/projects/${projectId}/save`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(finalPayload),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        console.error("Ошибка API сохранения:", errorData);
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          console.error("Ошибка API сохранения (текст):", errorText);
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log(
      "[saveConstructionDesignPreservingData] Результат сохранения:",
      result,
    );

    let verified;
    try {
      verified = await getProjectEstimate(projectId);
    } catch (err) {
      throw new Error(
        `Смета сохранена, но не удалось проверить состав разделов (${err?.message || err}). Не обновляйте страницу и нажмите «Сохранить» ещё раз.`,
      );
    }
    const actualIds = new Set(
      collectEstimateSectionRows(verified)
        .map(readSectionRowId)
        .filter((id) => id != null),
    );
    const lost = [...readPayloadSectionIds(finalPayload)].filter(
      (id) => !actualIds.has(id),
    );
    if (lost.length > 0) {
      const labels = lost
        .map((id) => sectionLabel(id, sectionNameById))
        .join(", ");
      throw sectionsLostAfterWriteError(labels);
    }

    return result;
  } catch (error) {
    console.error("Error saving construction design data:", error);
    throw error;
  }
};

// Сохранить данные строительного проектирования
export const saveConstructionDesign = async (projectId, data) => {
  try {
    const response = await fetch(
      `${getAPIURL()}project_estimates/projects/${projectId}/save`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(data),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        // Если не удалось получить JSON, используем текст
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error("Error saving construction design data:", error);
    throw error;
  }
};

// Обновить процент "На разработку" (threshold cost percent)
export const updateThresholdCostPercent = async (projectId, percent) => {
  try {
    const normalizedPercent =
      typeof percent === "number" ? percent : Number(percent);

    const response = await fetch(
      `${getAPIURL()}project_estimates/${projectId}/threshold-cost-percent`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(
          Number.isFinite(normalizedPercent) ? normalizedPercent : 0,
        ),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating threshold cost percent:", error);
    throw error;
  }
};

// Сохранить данные конкретной секции строительного проектирования
export const saveSectionConstructionDesign = async (
  projectId,
  sectionId,
  data,
) => {
  try {
    // Подготавливаем данные в формате, ожидаемом новым API
    const saveData = {
      bonus_percent: data.bonus_percent || 0,
      average_salary: data.average_salary || 0,
      block_expenditures:
        data.block_expenditures?.map((block) => ({
          block: {
            id: block.block?.id || 0,
            name: block.block?.name || "",
            tei_id: block.block?.tei_id || 0,
          },
          floor_count: block.floor_count || 0,
          day_efforts: block.day_efforts || 0,
          bonus_day_efforts: block.bonus_day_efforts || 0,
          money_expenditures: block.money_expenditures || 0,
          bonus_money_expenditures: block.bonus_money_expenditures || 0,
        })) || [],
    };

    const response = await fetch(
      `${getAPIURL()}project_estimates/projects/${projectId}/sections/${sectionId}/construction_design`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(saveData),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        // Если не удалось получить JSON, используем текст
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error saving section construction design data:", error);
    throw error;
  }
};

// Получить все компании
export const getAllCompanies = async () => {
  try {
    const response = await fetch(`${getAPIURL()}companies/all`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching companies:", error);
    throw error;
  }
};

// Получить коммерческое предложение для проекта
// @param {number} projectId - ID проекта
// @param {number|null} version - Опциональный номер версии для получения конкретной версии
export const getCommercialProposal = async (projectId, version = null) => {
  try {
    // Добавляем timestamp для предотвращения кэширования
    const timestamp = new Date().getTime();

    // Формируем URL с опциональным параметром version
    let url = `${getAPIURL()}project_estimates/projects/${projectId}/commercial_proposal?_t=${timestamp}`;
    if (version !== null && version !== undefined) {
      url += `&version=${version}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
      },
      credentials: "include",
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        // Если не удалось получить JSON, используем текст
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    if (!String(error.message || "").includes("404")) {
      console.error("Error fetching commercial proposal:", error);
    }
    throw error;
  }
};

// Загрузить изображение схемы блокировки для проекта
export const uploadProjectImage = async (projectId, imageFile) => {
  try {
    const formData = new FormData();
    formData.append("image", imageFile);

    const response = await fetch(
      `${getAPIURL()}project_estimates/cp_teis/${projectId}/upload_image`,
      {
        method: "POST",
        headers: {
          // Не устанавливаем Content-Type, так как FormData автоматически установит правильный заголовок с boundary
        },
        credentials: "include",
        body: formData,
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        // Если не удалось получить JSON, используем текст
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error("Error uploading project image:", error);
    throw error;
  }
};

// Сохранить заголовок коммерческого предложения
export const saveCpTitle = async (projectId, data) => {
  try {
    const url = `${getAPIURL()}project_estimates/projects/${projectId}/cp_title/save`;
    const requestBody = JSON.stringify(data);

    console.log("Saving CP title:", {
      url,
      projectId,
      data,
      requestBody,
      dataLength: requestBody.length,
    });

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: requestBody,
    });

    console.log("Response status:", response.status, response.statusText);

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      let errorDetails = null;

      // Пытаемся получить заголовки ответа
      const contentType = response.headers.get("content-type");
      console.log("Response Content-Type:", contentType);

      // Читаем тело ответа как текст (можно прочитать только один раз)
      try {
        const responseText = await response.text();
        errorDetails = responseText;

        // Если Content-Type указывает на JSON, пытаемся распарсить
        if (contentType && contentType.includes("application/json")) {
          try {
            const errorData = JSON.parse(responseText);
            errorMessage += ` - ${JSON.stringify(errorData)}`;
            console.error("Error response JSON:", errorData);
          } catch (parseError) {
            // Если не удалось распарсить, используем текст
            errorMessage += ` - ${responseText}`;
            console.error(
              "Error response text (failed to parse as JSON):",
              responseText,
            );
          }
        } else {
          errorMessage += ` - ${responseText}`;
          console.error("Error response text:", responseText);
        }
      } catch (e) {
        console.error("Could not read error response body:", e);
      }

      console.error("Error response details:", {
        status: response.status,
        statusText: response.statusText,
        contentType,
        details: errorDetails,
        url,
      });

      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log("Successfully saved CP title:", result);
    return result;
  } catch (error) {
    console.error("Error saving CP title:", error);
    throw error;
  }
};

// Сохранить график платежей
export const saveCpPaymentSchedule = async (projectId, paymentSchedule) => {
  try {
    const response = await fetch(
      `${getAPIURL()}project_estimates/projects/${projectId}/cp_payment_schedule/save`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(paymentSchedule),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        // Если не удалось получить JSON, используем текст
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error saving CP payment schedule:", error);
    throw error;
  }
};

// Сохранить доп. строки / надбавки коммерческого предложения (полная замена списка)
export const saveCpAdditionalItems = async (projectId, items) => {
  try {
    const response = await fetch(
      `${getAPIURL()}project_estimates/projects/${projectId}/cp_additional_items/save`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(items),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error saving CP additional items:", error);
    throw error;
  }
};

// Сохранить текстовые блоки КП (заголовок + пункты). Полная замена списка.
export const saveCpInfoSections = async (projectId, sections) => {
  try {
    const response = await fetch(
      `${getAPIURL()}project_estimates/projects/${projectId}/cp_info_sections/save`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(sections),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error saving CP info sections:", error);
    throw error;
  }
};

// Сохранить настройки проекта (блоки, площадь участка, коэффициенты, схема)
export const saveProjectBlocks = async (projectId, data) => {
  try {
    const response = await fetch(
      `${getAPIURL()}project_estimates/cp_teis/${projectId}/blocks/save`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(data),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        // Если не удалось получить JSON, используем текст
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error saving project blocks:", error);
    throw error;
  }
};

/**
 * Отправить коммерческое предложение на согласование
 * @param {number} projectId - ID проекта
 * @param {Array<number>} approverIds - Массив ID согласующих
 * @param {number} totalPrice - Общая стоимость проекта
 * @returns {Promise<Object>} Результат отправки
 */
export const requestCommercialProposalApproval = async (
  projectId,
  approverIds,
  totalPrice,
) => {
  try {
    const response = await fetch(
      `${getAPIURL()}project_estimates/projects/${projectId}/commercial_proposal/request_for_approval`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          approver_ids: approverIds,
          total_price: totalPrice,
        }),
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Ответ сервера: ${response.status} ${response.statusText}`);
      console.error(`Тело ответа: ${errorText}`);
      throw new Error(`Ошибка при отправке на согласование: ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при отправке на согласование:", error);
    throw error;
  }
};

/**
 * Получить историю версий согласований коммерческого предложения
 * @param {number} projectId - ID проекта
 * @returns {Promise<Array>} Массив версий согласований
 */
export const getCommercialProposalApprovals = async (projectId) => {
  try {
    // Добавляем timestamp для предотвращения кэширования
    const timestamp = new Date().getTime();
    const response = await fetch(
      `${getAPIURL()}project_estimates/projects/${projectId}/commercial_proposal_approvals?_t=${timestamp}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при получении истории согласований:", error);
    throw error;
  }
};

/**
 * Отменить отправку коммерческого предложения на согласование
 * @param {number} projectId - ID проекта
 * @returns {Promise<Object>} Результат отмены согласования
 */
export const cancelCommercialProposalApproval = async (projectId) => {
  try {
    const response = await fetch(
      `${getAPIURL()}project_estimates/projects/${projectId}/commercial_proposal/cancel_approval_request`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Ответ сервера: ${response.status} ${response.statusText}`);
      console.error(`Тело ответа: ${errorText}`);
      throw new Error(
        `Ошибка при отмене согласования: ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при отмене согласования:", error);
    throw error;
  }
};

/**
 * Отправить смету на согласование
 * @param {number} projectId - ID проекта
 * @param {Array<number>} approverIds - Массив ID согласующих
 * @param {number} totalPrice - Общая стоимость проекта
 * @returns {Promise<Object>} Результат отправки
 */
export const requestProjectEstimateApproval = async (
  projectId,
  approverIds,
  totalPrice,
) => {
  try {
    const response = await fetch(
      `${getAPIURL()}project_estimates/projects/${projectId}/request_for_approval`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          approver_ids: approverIds,
          total_price: totalPrice,
        }),
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Ответ сервера: ${response.status} ${response.statusText}`);
      console.error(`Тело ответа: ${errorText}`);
      throw new Error(`Ошибка при отправке на согласование: ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при отправке на согласование:", error);
    throw error;
  }
};

/**
 * Получить историю согласований сметы
 * @param {number} projectId - ID проекта
 * @returns {Promise<Array>} Массив версий согласований
 */
export const getProjectEstimateApprovals = async (projectId) => {
  try {
    const response = await fetch(
      `${getAPIURL()}project_estimates/projects/${projectId}/approvals`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при получении истории согласований:", error);
    throw error;
  }
};

/**
 * Восстановить версию ТЭП, Сметы и КП
 * @param {number} projectId - ID проекта
 * @param {number|null} version - Версия для восстановления (null для создания новой копии текущей версии)
 * @returns {Promise<Object>} Результат восстановления версии с полями message и version
 */
export const restoreVersion = async (projectId, version = null) => {
  try {
    // Формируем body: если version === null, не отправляем поле version
    const body = version !== null ? { version: version } : {};

    const response = await fetch(
      `${getAPIURL()}project_estimates/project/${projectId}/restore_version`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify(body),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при восстановлении версии:", error);
    throw error;
  }
};

/**
 * Получить коммерческое предложение для проекта эскизного проектирования
 * @param {number} projectId - ID проекта
 * @returns {Promise<Object>} Данные коммерческого предложения
 */
export const getSketchDesignCommercialOffer = async (projectId) => {
  try {
    const timestamp = new Date().getTime();
    const response = await fetch(
      `${getAPIURL()}sketch_design/commercial_offer/calculate/${projectId}?_t=${timestamp}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching sketch design commercial offer:", error);
    throw error;
  }
};

/**
 * Рассчитать смету проекта эскизного проектирования
 * GET sketch_design/estimate/calculate/{project_id}
 * @param {number} projectId - ID проекта
 * @returns {Promise<Object>} Данные сметы
 */
export const getSketchDesignEstimate = async (projectId) => {
  try {
    const timestamp = new Date().getTime();
    const response = await fetch(
      `${getAPIURL()}sketch_design/estimate/calculate/${projectId}?_t=${timestamp}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {}
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching sketch design estimate:", error);
    throw error;
  }
};

/**
 * Обновить смету проекта эскизного проектирования
 * PATCH sketch_design/estimate/update/{project_id}
 * @param {number} projectId - ID проекта
 * @param {Object} body - Тело обновления сметы
 * @returns {Promise<Object>} Обновленные данные сметы
 */
export const updateSketchDesignEstimate = async (projectId, body) => {
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/estimate/update/${projectId}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify(body),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {}
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating sketch design estimate:", error);
    throw error;
  }
};

/**
 * Получить виды работ сметы ЭП
 * GET sketch_design/estimate/work_types/get/{project_id}
 */
export const getSketchDesignEstimateWorkTypes = async (projectId) => {
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/estimate/work_types/get/${projectId}`,
      {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "include",
      },
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(
        err.detail ? JSON.stringify(err.detail) : `HTTP ${response.status}`,
      );
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching sketch design estimate work types:", error);
    throw error;
  }
};

/**
 * Справочник статей Finansist (fact_stream) для ЭП
 * GET sketch_design/fin_job_type
 * @returns {Promise<Array<{ id: number, fact_stream_id: number, name: string }>>}
 */
export const getSketchDesignFinJobTypes = async () => {
  try {
    const response = await fetch(`${getAPIURL()}sketch_design/fin_job_type`, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "include",
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching sketch design fin job types:", error);
    throw error;
  }
};

/**
 * Добавить вид работ в смету ЭП
 * POST sketch_design/estimate/work_types/add/{project_id}
 * На бэкенде встречаются разные имена поля в схеме и в вызове менеджера — шлём оба алиаса.
 * @param {{ name: string, work_fin_job_type_id?: number, fin_job_type?: number, is_reserve?: boolean }} body
 */
export const addSketchDesignEstimateWorkType = async (projectId, body) => {
  const articleId = Number(body.work_fin_job_type_id ?? body.fin_job_type);
  const payload = {
    ...body,
    ...(Number.isFinite(articleId) && articleId > 0
      ? { work_fin_job_type_id: articleId, fin_job_type: articleId }
      : {}),
  };
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/estimate/work_types/add/${projectId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify(payload),
      },
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(
        err.detail ? JSON.stringify(err.detail) : `HTTP ${response.status}`,
      );
    }
    return await response.json();
  } catch (error) {
    console.error("Error adding sketch design estimate work type:", error);
    throw error;
  }
};

/**
 * Обновить вид работ сметы ЭП
 * PATCH sketch_design/estimate/work_types/update/{work_type_id}
 */
export const updateSketchDesignEstimateWorkType = async (workTypeId, body) => {
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/estimate/work_types/update/${workTypeId}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify(body),
      },
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(
        err.detail ? JSON.stringify(err.detail) : `HTTP ${response.status}`,
      );
    }
    return await response.json();
  } catch (error) {
    console.error("Error updating sketch design estimate work type:", error);
    throw error;
  }
};

/**
 * Удалить вид работ сметы ЭП
 * DELETE sketch_design/estimate/work_types/delete/{work_type_id}
 */
export const deleteSketchDesignEstimateWorkType = async (workTypeId) => {
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/estimate/work_types/delete/${workTypeId}`,
      {
        method: "DELETE",
        headers: { Accept: "application/json" },
        credentials: "include",
      },
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(
        err.detail ? JSON.stringify(err.detail) : `HTTP ${response.status}`,
      );
    }
    const text = await response.text();
    return text ? JSON.parse(text) : {};
  } catch (error) {
    console.error("Error deleting sketch design estimate work type:", error);
    throw error;
  }
};

/**
 * Получить список названий видов работ для сметы ЭП
 * GET sketch_design/estimate/work_types/names
 * @returns {Promise<Array<{ id: number, name: string, work_fin_job_type_id: number }>>}
 */
export const getSketchDesignEstimateWorkTypeNames = async () => {
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/estimate/work_types/names`,
      {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "include",
      },
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error(
      "Error fetching sketch design estimate work type names:",
      error,
    );
    throw error;
  }
};

/**
 * Получить этапы оплаты для коммерческого предложения эскизного проектирования
 * @param {number} projectId - ID проекта
 * @returns {Promise<Array>} Массив этапов оплаты
 */
export const getSketchDesignPaymentProcedures = async (projectId) => {
  try {
    const timestamp = new Date().getTime();
    const response = await fetch(
      `${getAPIURL()}sketch_design/commercial_offer/payment_procedure/get/${projectId}?_t=${timestamp}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching sketch design payment procedures:", error);
    throw error;
  }
};

/**
 * Обновить основные параметры КП эскизного проектирования
 * PATCH sketch_design/commercial_offer/update/{project_id}
 * @param {number} projectId - ID проекта
 * @param {{ counterparty_id?: number, receiver_id?: number, deadline_days?: number, deadline_description?: string, title?: string, nds_percent?: number, discount_percent?: number, discount_amount?: number }} data
 * @returns {Promise<Object>} Обновлённое КП
 */
export const updateSketchDesignCommercialOffer = async (projectId, data) => {
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/commercial_offer/update/${projectId}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify(data),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.detail
          ? JSON.stringify(errorData.detail)
          : `HTTP ${response.status}`,
      );
    }
    return await response.json();
  } catch (error) {
    console.error("Error updating sketch design commercial offer:", error);
    throw error;
  }
};

/**
 * Создать этап оплаты для КП эскизного проектирования
 * POST sketch_design/commercial_offer/payment_procedure/add/{project_id}
 * @param {number} projectId - ID проекта
 * @param {{ stage: string, percentage_ratio: number, deadline: string }} data
 */
export const addSketchDesignPaymentProcedure = async (projectId, data) => {
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/commercial_offer/payment_procedure/add/${projectId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify(data),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.detail
          ? JSON.stringify(errorData.detail)
          : `HTTP ${response.status}`,
      );
    }
    return await response.json();
  } catch (error) {
    console.error("Error adding sketch design payment procedure:", error);
    throw error;
  }
};

/**
 * Обновить процент этапа оплаты
 * PATCH sketch_design/commercial_offer/payment_procedure/update/{project_id}
 * @param {number} projectId - ID проекта
 * @param {{ payment_procedure_id: number, percentage_ratio: number }} data
 */
export const updateSketchDesignPaymentProcedure = async (projectId, data) => {
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/commercial_offer/payment_procedure/update/${projectId}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify(data),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.detail
          ? JSON.stringify(errorData.detail)
          : `HTTP ${response.status}`,
      );
    }
    return await response.json();
  } catch (error) {
    console.error("Error updating sketch design payment procedure:", error);
    throw error;
  }
};

/**
 * Удалить этап оплаты
 * DELETE sketch_design/commercial_offer/payment_procedure/delete/{payment_procedure_id}
 * @param {number} paymentProcedureId - ID этапа оплаты
 */
export const deleteSketchDesignPaymentProcedure = async (
  paymentProcedureId,
) => {
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/commercial_offer/payment_procedure/delete/${paymentProcedureId}`,
      {
        method: "DELETE",
        headers: { Accept: "application/json" },
        credentials: "include",
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.detail
          ? JSON.stringify(errorData.detail)
          : `HTTP ${response.status}`,
      );
    }
    return await response
      .text()
      .then((t) => (t ? JSON.parse(t) : null))
      .catch(() => null);
  } catch (error) {
    console.error("Error deleting sketch design payment procedure:", error);
    throw error;
  }
};

/**
 * Получить статус согласования по проекту ЭП и разделу
 * GET sketch_design/approvals/get/{project_id}/{chapter}
 * @param {number} projectId - ID проекта ЭП
 * @param {string} chapter - Раздел: "commercial_offer" | "estimate"
 * @returns {Promise<Object|null>} Данные согласования или null, если не найдено / ошибка
 */
export const getSketchDesignApproval = async (projectId, chapter) => {
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/approvals/get/${projectId}/${chapter}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
      },
    );
    if (response.status === 422 || response.status === 404) {
      return null;
    }
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.detail
          ? JSON.stringify(errorData.detail)
          : `HTTP ${response.status}`,
      );
    }
    return await response.json();
  } catch (error) {
    console.error("Ошибка при получении согласования ЭП:", error);
    return null;
  }
};

/**
 * Создать согласование по проекту ЭП и разделу
 * POST sketch_design/approvals/create/{project_id}/{chapter}
 * @param {number} projectId - ID проекта ЭП
 * @param {string} chapter - Раздел: "commercial_offer" | "estimate"
 * @param {number[]} approverUserIds - Массив ID пользователей-согласующих
 * @returns {Promise<Object>} Созданное согласование
 */
export const createSketchDesignApproval = async (
  projectId,
  chapter,
  approverUserIds,
) => {
  const response = await fetch(
    `${getAPIURL()}sketch_design/approvals/create/${projectId}/${chapter}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
      body: JSON.stringify(approverUserIds),
    },
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail
        ? JSON.stringify(errorData.detail)
        : `HTTP ${response.status}`,
    );
  }
  return response.json();
};

/**
 * Удалить согласование по проекту ЭП и разделу
 * DELETE sketch_design/approvals/delete/{project_id}/{chapter}
 * @param {number} projectId - ID проекта ЭП
 * @param {string} chapter - Раздел: "commercial_offer" | "estimate"
 */
export const deleteSketchDesignApproval = async (projectId, chapter) => {
  const response = await fetch(
    `${getAPIURL()}sketch_design/approvals/delete/${projectId}/${chapter}`,
    {
      method: "DELETE",
      headers: { Accept: "application/json" },
      credentials: "include",
    },
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail
        ? JSON.stringify(errorData.detail)
        : `HTTP ${response.status}`,
    );
  }
  return response.json().catch(() => null);
};

/**
 * Обновить статус согласующего
 * PATCH sketch_design/approvals/approver/status/{approver_id}/{status}
 * @param {number} approverId - ID записи согласующего (approver.id)
 * @param {string} status - approved | in_processing | rejected
 */
export const updateSketchDesignApproverStatus = async (approverId, status) => {
  const response = await fetch(
    `${getAPIURL()}sketch_design/approvals/approver/status/${approverId}/${status}`,
    {
      method: "PATCH",
      headers: { Accept: "application/json" },
      credentials: "include",
    },
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail
        ? JSON.stringify(errorData.detail)
        : `HTTP ${response.status}`,
    );
  }
  return response.json();
};

/**
 * Получить смету по договору с фактом
 * GET /fin_plan/projects/{project_id}/with_fact
 * Смета по договору плюс факт: трудозатраты по блокам, по разделам, суммарная фактическая стоимость и сумма сметы по договору.
 * @param {number} projectId - ID проекта
 * @param {string|null} dateFrom - Начало периода для факта (дата отчёта), формат YYYY-MM-DD
 * @param {string|null} dateTo - Конец периода для факта (дата отчёта), формат YYYY-MM-DD
 * @returns {Promise<Object>} Данные сметы с фактом
 */
export const getFinPlanWithFact = async (
  projectId,
  dateFrom = null,
  dateTo = null,
) => {
  try {
    const params = new URLSearchParams();
    if (dateFrom) {
      params.append("date_from", dateFrom);
    }
    if (dateTo) {
      params.append("date_to", dateTo);
    }

    const queryString = params.toString();
    const url = `${getAPIURL()}fin_plan/projects/${projectId}/with_fact${queryString ? `?${queryString}` : ""}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {}
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching fin plan with fact:", error);
    throw error;
  }
};

/**
 * Получить данные по блокам одного раздела с фактом
 * GET /fin_plan/projects/{project_id}/sections/{section_id}/blocks
 * Данные по блокам одного раздела: смета и факт по каждому блоку.
 * @param {number} projectId - ID проекта
 * @param {number} sectionId - ID раздела
 * @param {string|null} dateFrom - Начало периода для факта (дата отчёта), формат YYYY-MM-DD
 * @param {string|null} dateTo - Конец периода для факта (дата отчёта), формат YYYY-MM-DD
 * @returns {Promise<Object>} Данные по блокам раздела с фактом
 */
export const getFinPlanSectionBlocks = async (
  projectId,
  sectionId,
  dateFrom = null,
  dateTo = null,
) => {
  try {
    const params = new URLSearchParams();
    if (dateFrom) {
      params.append("date_from", dateFrom);
    }
    if (dateTo) {
      params.append("date_to", dateTo);
    }

    const queryString = params.toString();
    const url = `${getAPIURL()}fin_plan/projects/${projectId}/sections/${sectionId}/blocks${queryString ? `?${queryString}` : ""}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {}
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching fin plan section blocks:", error);
    throw error;
  }
};

/**
 * Получить фактические трудозатраты в формате табеля
 * GET /fin_plan/projects/{project_id}/fact_timesheet
 * Фактические трудозатраты performers помесячно и по дням (часы).
 * Источник тот же, что факт разделов РП в with_fact.
 * @param {number} projectId - ID проекта
 * @param {Object} [options]
 * @param {number|null} [options.sectionId] - Фильтр по разделу (PortalJobType.project_section_id)
 * @param {number|null} [options.userId] - Фильтр по исполнителю
 * @param {string|null} [options.dateFrom] - Начало периода (YYYY-MM-DD)
 * @param {string|null} [options.dateTo] - Конец периода (YYYY-MM-DD)
 * @returns {Promise<Object>} Табель фактических трудозатрат
 */
export const getFinPlanFactTimesheet = async (
  projectId,
  { sectionId = null, userId = null, dateFrom = null, dateTo = null } = {},
) => {
  try {
    const params = new URLSearchParams();
    if (sectionId != null && sectionId !== "") {
      params.append("section_id", sectionId);
    }
    if (userId != null && userId !== "") {
      params.append("user_id", userId);
    }
    if (dateFrom) {
      params.append("date_from", dateFrom);
    }
    if (dateTo) {
      params.append("date_to", dateTo);
    }

    const queryString = params.toString();
    const url = `${getAPIURL()}fin_plan/projects/${projectId}/fact_timesheet${queryString ? `?${queryString}` : ""}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {}
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching fin plan fact timesheet:", error);
    throw error;
  }
};

const parseEstimateApiError = async (response, fallbackMessage) => {
  try {
    const data = await response.json();
    const detail = data?.detail;
    if (typeof detail === "string" && detail.trim()) return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      return detail
        .map((item) => item?.msg || item?.message || JSON.stringify(item))
        .join("; ");
    }
    if (typeof data?.message === "string" && data.message.trim()) {
      return data.message;
    }
    return fallbackMessage;
  } catch (err) {
    return fallbackMessage;
  }
};

/**
 * История корректировок трудозатрат по разделу.
 * GET /project_estimates/projects/{project_id}/sections/{section_id}/corrections
 */
export const getSectionLaborEffortCorrections = async (
  projectId,
  sectionId,
) => {
  const response = await fetch(
    `${getAPIURL()}project_estimates/projects/${projectId}/sections/${sectionId}/corrections`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
    },
  );

  if (response.status === 404) return [];

  if (!response.ok) {
    throw new Error(
      await parseEstimateApiError(
        response,
        `Не удалось загрузить корректировки раздела ${sectionId}`,
      ),
    );
  }

  const data = await response.json();
  return Array.isArray(data) ? data : [];
};

/**
 * Журнал изменений корректировок трудозатрат по разделу.
 * GET /project_estimates/projects/{project_id}/sections/{section_id}/corrections/history
 * @param {number|null} [version]
 */
export const getSectionLaborEffortCorrectionsHistory = async (
  projectId,
  sectionId,
  version = null,
) => {
  let url = `${getAPIURL()}project_estimates/projects/${projectId}/sections/${sectionId}/corrections/history`;
  if (version !== null && version !== undefined) {
    url += `?version=${version}`;
  }

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    credentials: "include",
  });

  if (response.status === 404) return [];

  if (!response.ok) {
    throw new Error(
      await parseEstimateApiError(
        response,
        `Не удалось загрузить историю корректировок раздела ${sectionId}`,
      ),
    );
  }

  const history = await response.json();
  return Array.isArray(history) ? history : [];
};

/**
 * Создать корректировку трудозатрат раздела.
 * POST /project_estimates/projects/{project_id}/sections/{section_id}/corrections
 * tei_id обязателен для разделов с блоками. Для проектных разделов (ГП, Инсоляция) поле не передаётся.
 * @param {{ tei_id?: number|null, day_efforts: number, comment?: string }} payload
 */
export const saveSectionLaborEffortCorrection = async (
  projectId,
  sectionId,
  payload,
) => {
  const body = {
    day_efforts: Number(payload?.day_efforts) || 0,
  };
  if (payload?.tei_id != null && payload.tei_id !== "") {
    body.tei_id = Number(payload.tei_id);
  }
  if (payload?.comment != null && payload.comment !== "") {
    body.comment = payload.comment;
  }

  const response = await fetch(
    `${getAPIURL()}project_estimates/projects/${projectId}/sections/${sectionId}/corrections`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    throw new Error(
      await parseEstimateApiError(
        response,
        "Не удалось сохранить корректировку трудозатрат",
      ),
    );
  }

  return response.json();
};

/**
 * Обновить корректировку трудозатрат.
 * PATCH /project_estimates/projects/{project_id}/corrections/{correction_id}
 * @param {{ day_efforts?: number, comment?: string }} payload
 */
export const patchLaborEffortCorrection = async (
  projectId,
  correctionId,
  payload,
) => {
  const body = {};
  if (payload?.day_efforts !== undefined) {
    body.day_efforts = Number(payload.day_efforts) || 0;
  }
  if (payload?.comment !== undefined) {
    body.comment = payload.comment;
  }

  const response = await fetch(
    `${getAPIURL()}project_estimates/projects/${projectId}/corrections/${correctionId}`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    throw new Error(
      await parseEstimateApiError(
        response,
        "Не удалось обновить корректировку трудозатрат",
      ),
    );
  }

  return response.json();
};

/**
 * Удалить корректировку трудозатрат.
 * DELETE /project_estimates/projects/{project_id}/corrections/{correction_id}
 */
export const deleteLaborEffortCorrection = async (projectId, correctionId) => {
  const response = await fetch(
    `${getAPIURL()}project_estimates/projects/${projectId}/corrections/${correctionId}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
    },
  );

  if (!response.ok) {
    throw new Error(
      await parseEstimateApiError(
        response,
        "Не удалось удалить корректировку трудозатрат",
      ),
    );
  }

  if (response.status === 204) return { message: "ok" };
  try {
    return await response.json();
  } catch (err) {
    return { message: "ok" };
  }
};

export const ESTIMATE_CORRECTION_MODULES = ["bim", "qc", "pd", "pm"];

const moduleCorrectionsUrl = (projectId, module) =>
  `${getAPIURL()}project_estimates/projects/${projectId}/modules/${module}/corrections`;

const moduleCorrectionItemUrl = (projectId, correctionId) =>
  `${getAPIURL()}project_estimates/projects/${projectId}/module-corrections/${correctionId}`;

/**
 * Список корректировок модуля (bim | qc | pd | pm).
 * GET /project_estimates/projects/{project_id}/modules/{module}/corrections
 */
export const getModuleLaborEffortCorrections = async (projectId, module) => {
  const response = await fetch(moduleCorrectionsUrl(projectId, module), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    credentials: "include",
  });

  if (response.status === 404) return [];

  if (!response.ok) {
    throw new Error(
      await parseEstimateApiError(
        response,
        `Не удалось загрузить корректировки модуля ${module}`,
      ),
    );
  }

  const data = await response.json();
  return Array.isArray(data) ? data : [];
};

/**
 * Журнал корректировок модуля.
 * GET /project_estimates/projects/{project_id}/modules/{module}/corrections/history
 * @param {number|null} [version]
 */
export const getModuleLaborEffortCorrectionsHistory = async (
  projectId,
  module,
  version = null,
) => {
  let url = `${moduleCorrectionsUrl(projectId, module)}/history`;
  if (version !== null && version !== undefined) {
    url += `?version=${version}`;
  }

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    credentials: "include",
  });

  if (response.status === 404) return [];

  if (!response.ok) {
    throw new Error(
      await parseEstimateApiError(
        response,
        `Не удалось загрузить историю корректировок модуля ${module}`,
      ),
    );
  }

  const history = await response.json();
  return Array.isArray(history) ? history : [];
};

/**
 * Сохранить корректировку трудозатрат модуля.
 * POST /project_estimates/projects/{project_id}/modules/{module}/corrections
 * @param {{ role: string, section_id?: number|null, day_efforts: number, comment?: string }} payload
 */
export const saveModuleLaborEffortCorrection = async (
  projectId,
  module,
  payload,
) => {
  const body = {
    role: String(payload?.role || ""),
    day_efforts: Number(payload?.day_efforts) || 0,
  };
  if (payload?.section_id != null && payload.section_id !== "") {
    body.section_id = Number(payload.section_id);
  }
  if (payload?.comment != null && payload.comment !== "") {
    body.comment = payload.comment;
  }

  const response = await fetch(moduleCorrectionsUrl(projectId, module), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    credentials: "include",
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(
      await parseEstimateApiError(
        response,
        "Не удалось сохранить корректировку модуля",
      ),
    );
  }

  return response.json();
};

/**
 * Обновить корректировку модуля.
 * PATCH /project_estimates/projects/{project_id}/module-corrections/{correction_id}
 * @param {{ day_efforts?: number, comment?: string }} payload
 */
export const patchModuleLaborEffortCorrection = async (
  projectId,
  correctionId,
  payload,
) => {
  const body = {};
  if (payload?.day_efforts !== undefined) {
    body.day_efforts = Number(payload.day_efforts) || 0;
  }
  if (payload?.comment !== undefined) {
    body.comment = payload.comment;
  }

  const response = await fetch(
    moduleCorrectionItemUrl(projectId, correctionId),
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    throw new Error(
      await parseEstimateApiError(
        response,
        "Не удалось обновить корректировку модуля",
      ),
    );
  }

  return response.json();
};

/**
 * Удалить одну корректировку модуля.
 * DELETE /project_estimates/projects/{project_id}/module-corrections/{correction_id}
 */
export const deleteModuleLaborEffortCorrection = async (
  projectId,
  correctionId,
) => {
  const response = await fetch(
    moduleCorrectionItemUrl(projectId, correctionId),
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
    },
  );

  if (!response.ok) {
    throw new Error(
      await parseEstimateApiError(
        response,
        "Не удалось удалить корректировку модуля",
      ),
    );
  }

  if (response.status === 204) return { message: "ok" };
  try {
    return await response.json();
  } catch (err) {
    return { message: "ok" };
  }
};

const projectIncomeCorrectionUrl = (projectId) =>
  `${getAPIURL()}project_estimates/projects/${projectId}/income-correction`;

/**
 * Корректировки дохода проекта.
 * GET /project_estimates/projects/{project_id}/income-correction
 * @param {number|null} [version]
 */
export const getProjectIncomeCorrection = async (projectId, version = null) => {
  let url = projectIncomeCorrectionUrl(projectId);
  if (version !== null && version !== undefined) {
    url += `?version=${version}`;
  }

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    credentials: "include",
  });

  if (response.status === 404) return [];

  if (!response.ok) {
    throw new Error(
      await parseEstimateApiError(
        response,
        "Не удалось загрузить корректировки дохода",
      ),
    );
  }

  const data = await response.json();
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (data?.id != null) return [data];
  return [];
};

/**
 * Добавить корректировку дохода проекта.
 * POST /project_estimates/projects/{project_id}/income-correction
 * @param {{ amount: number, nds_included_in_amount: boolean, nds_percent?: number, comment?: string }} payload
 */
export const saveProjectIncomeCorrection = async (projectId, payload) => {
  const body = {
    amount: Number(payload?.amount) || 0,
    nds_included_in_amount: Boolean(payload?.nds_included_in_amount),
    nds_percent: Number(payload?.nds_percent) || 0,
  };
  if (payload?.comment != null && payload.comment !== "") {
    body.comment = payload.comment;
  }

  const response = await fetch(projectIncomeCorrectionUrl(projectId), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    credentials: "include",
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(
      await parseEstimateApiError(
        response,
        "Не удалось сохранить корректировку дохода",
      ),
    );
  }

  return response.json();
};

/**
 * Удалить корректировку дохода проекта.
 * DELETE /project_estimates/projects/{project_id}/income-correction/{correction_id}
 */
export const deleteProjectIncomeCorrection = async (
  projectId,
  correctionId,
) => {
  const response = await fetch(
    `${projectIncomeCorrectionUrl(projectId)}/${correctionId}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
    },
  );

  if (response.status === 404) return { message: "ok" };

  if (!response.ok) {
    throw new Error(
      await parseEstimateApiError(
        response,
        "Не удалось удалить корректировку дохода",
      ),
    );
  }

  if (response.status === 204) return { message: "ok" };
  try {
    return await response.json();
  } catch (err) {
    return { message: "ok" };
  }
};

/**
 * Журнал изменений корректировки дохода.
 * GET /project_estimates/projects/{project_id}/income-correction/history
 * @param {number|null} [version]
 */
export const getProjectIncomeCorrectionHistory = async (
  projectId,
  version = null,
) => {
  let url = `${projectIncomeCorrectionUrl(projectId)}/history`;
  if (version !== null && version !== undefined) {
    url += `?version=${version}`;
  }

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    credentials: "include",
  });

  if (response.status === 404) return [];

  if (!response.ok) {
    throw new Error(
      await parseEstimateApiError(
        response,
        "Не удалось загрузить историю корректировки дохода",
      ),
    );
  }

  const history = await response.json();
  return Array.isArray(history) ? history : [];
};

